import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle, ArrowLeft, BookOpen, CheckCircle2, FileText, FolderOpen, FolderUp,
    HardHat, Lock, Plus, Printer, RefreshCw, Unlock, Wand2,
} from 'lucide-react';
import { DokumentePanel } from '../dokumente/DokumentePanel';
import { formatDateLocal } from '../../services/dateService';
import { holdIdleExit } from '../../services/idleHold';
import { readOnlyMessage, readOnlyTitle } from '../../services/readOnlyNotice';
import { getRepresentatives } from '../../services/projectRepresentatives';
import {
    createHandbuchGewerke,
    createHandbuchNachweise,
    ensureHandbuch,
    freigebenHandbuch,
    neueFassungHandbuch,
    updateHandbuch,
    updateHandbuchGewerk,
} from '../../services/handbuchService';
import type { SchreibKontext } from '../../services/handbuchService';
import { ladeEinweisungQrFuerProjekt } from '../../services/sigeEinweisungService';
import {
    freigabeWiederholung, halteFreigabeFest, legeUnterlageAb, type FreigabeFesthalten, type FreigabeJournal,
} from '../../services/unterlagenAblage';
import { alleBuchbarenPositionen } from '../../services/billingSelection';
import { queueJournalMirror } from '../../services/serviceJournal';
import { useRechte } from '../../hooks/useRechte';
import { BillingSelectionModal } from '../BillingSelectionModal';
import { useBestandteilAbschluss } from '../bestandteile/useBestandteilAbschluss';
import { BestandteilAbschlussHinweis } from '../bestandteile/BestandteilAbschlussHinweis';
import { BestandteilStundenPflicht } from '../bestandteile/BestandteilStundenPflicht';
import { PAKET_NICHT_DIREKT, paketPositionIds, schonGebuchtHinweis, vollGebuchtePauschalen } from '../bestandteile/abschlussLogic';
import { planeHandbuchBuchung } from './handbuchBuchung';
import { projektDesVorgangs, verworfenText } from '../bestandteile/abschlussSchreibweg';
import type { Report } from '../../types';
import {
    ablageFehler, ablageStandAus, ablageStandText, ablageStandTon, pruefeAblageGroesse,
    type AblageStand, type AblageTon,
} from '../../services/ablageHilfen';
import {
    ablageHinweise, ablageWiederholung, baueHandbuchAblageAuftrag, entwurfsFassung, fassungAusFreigabe,
    fassungDerGeltendenFreigabe, handbuchFassungRef, mitInhaltsStand, sollHandbuchAblegen,
    type HandbuchAblageAuftrag, type HandbuchFassung,
} from './handbuchAblage';
import { AbweichungsHinweis } from '../common/AbweichungsHinweis';
import {
    aushangDatenAus,
    aushangDateiname,
    baueAushangPdf,
    baueHandbuchPdf,
    gefaehrdungenJeSchritt,
    handbuchDateiname,
    massnahmenJeGefaehrdung,
    pdfAlsBlob,
    projektAngabenAus,
    speicherePdf,
} from './handbuchIntegration';
import type { AushangDaten, AushangFormat, HandbuchPdfDaten } from './handbuchIntegration';
import { ORG_MODELLE, ORG_MODELL_REIHENFOLGE, orgModellLabel } from './orgModelle';
import {
    abgleicheGewerke,
    entwurfAusGewerk,
    freigabeEintrag,
    gemeinsameEinrichtungen,
    istGesperrt,
    kapitelBefunde,
    katalogHinweis,
    nachweisEntwuerfe,
    nachweisUebersicht,
    naechsteFassungsNummer,
    pruefeFreigabe,
    kapitelTitel,
    leereEinrichtung,
    seitenPlan,
    sperrHinweis,
    verbinde,
    vollstaendigkeitsQuote,
} from './handbuchLogic';
import { baueHandbuchKontext } from '../../services/aiAssistContext';
import { ladePlanungsphaseFuerKi } from '../../services/aiAssistPlanung';
import {
    LEERE_EINRICHTUNG_HERKUNFT,
    LEERE_HERKUNFT,
    einrichtungHerkunftSatz,
    herkunftSatz,
    vereineEinrichtungHerkunft,
    vereineHerkunft,
    type EinrichtungHerkunft,
    type KiBruecke,
    type KiOeffnung,
    type KiUebernahme,
    type OrdnungHerkunft,
} from '../sige-plan/kiAssistentLogic';
import {
    EINRICHTUNGSFELDER,
    bausteineZuFeld,
    druckZeilen,
    einrichtungsVorschlag,
    fuegeBausteinEin,
    wendeVorschlagAn,
} from './einrichtungBausteine';
import type { Baustein, EinrichtungsVorschlag } from './einrichtungBausteine';
import { KapitelZeichen } from './kapitel/StatusBadge';
import { SeitenKopf } from './kapitel/SeitenKopf';
import { SeitenFuss } from './kapitel/SeitenFuss';
import { Deckblatt } from './kapitel/Deckblatt';
import { Organigramm } from './kapitel/Organigramm';
import { Baustelleneinrichtung } from './kapitel/Baustelleneinrichtung';
import { Baustellenordnung } from './kapitel/Baustellenordnung';
import { Gefaehrdungsmatrix } from './kapitel/Gefaehrdungsmatrix';
import { Gesamtzeitplan } from './kapitel/Gesamtzeitplan';
import { GewerkSeite } from './kapitel/GewerkSeite';
import { Dokumentenverzeichnis } from './kapitel/Dokumentenverzeichnis';
import { Historie } from './kapitel/Historie';
import { useHandbuch } from './useHandbuch';
import { useProjektSteckbrief } from '../../services/projektSteckbrief';
import type { SeitenEintrag } from './handbuchLogic';
import type {
    Baustelleneinrichtung as EinrichtungDaten,
    Baustellenordnung as OrdnungDaten,
    Handbuch,
    HandbuchGewerk,
    OrgModell,
} from './types';
import type { ProjectGlobalData, ReportMetadata, VorankuendigungData } from '../../types';

/**
 * Wohin ein Sprungknopf aus einem Kapitel fuehrt.
 *
 * Die Kapitel benennen ihr Ziel FACHLICH („der Lageplan fehlt"), nicht
 * technisch. Die Uebersetzung in einen Modulschluessel steht hier an genau
 * einer Stelle — sonst muesste jedes der neun Blaetter die Navigation der
 * Anwendung kennen.
 */
type SprungZiel =
    | 'stammdaten'
    | 'verteiler'
    | 'vorankuendigung'
    | 'sigeplan'
    | 'notrufplan'
    | 'lageplan'
    | 'handbuch';

/** Die Modulschluessel, die dieses Modul anspringt. Deckungsgleich mit den
 *  Zweigen in `App.executeModuleNavigation`. */
export type HandbuchNavigationsZiel = 'editor' | 'sigeplan' | 'vorank' | 'koordinierung';

interface Props {
    projectId: string;
    /**
     * Projektstammdaten. Die Kapitel lesen sie NUR — Deckblatt, Organigramm
     * und Aushang schreiben nichts zurueck (Praezedenzfall Vorankuendigung:
     * „liest die Projektdaten nur, sie schreibt nie hinein").
     */
    metadata: ReportMetadata;
    /** `globalData` fuer Bauzeit und Ortskoordinaten. */
    globalData?: ProjectGlobalData;
    /** `report.vorankuendigung` fuer den §-2-Kasten des Organigramms und die
     *  Anhang-II-Vorbelegung der Gewerkeseiten. */
    vorankuendigung?: VorankuendigungData;
    userProfile?: any;
    /** Firmenstammdaten aus dem Profil (App.invoiceCompany). */
    company?: unknown;
    /** Briefkopf-Konfiguration (App.quoteConfigMemo). */
    quoteConfig?: unknown;
    /** Rechnungs-Stammdaten fuer die Fusszeile (App.invoiceConfigMemo). */
    invoiceConfig?: unknown;
    onBack: () => void;
    /**
     * Sprung in ein anderes Projektmodul.
     *
     * Fehlt der Rueckruf, entfaellt der jeweilige Knopf — der Satz, der die
     * Luecke benennt, bleibt stehen. Verdrahtet wird er in `src/App.tsx` mit
     * `executeModuleNavigation` und ausdruecklich NICHT mit
     * `handleModuleNavigation`: dessen Statuswechsel-Schleuse gilt fuer
     * `editor`/`module-sigeko-inspection`/`project-map` und wuerde beim Sprung
     * aus einem Kapitel einen Dialog „Projektstatus ändern?" aufwerfen, den
     * hier niemand bestellt hat.
     */
    onNavigate?: (modul: HandbuchNavigationsZiel) => void;
    /**
     * Sprung in den Projekt-Hub — dorthin, wo Stammdaten und Verteiler liegen.
     *
     * Bis dahin benutzte `springe` dafuer `onBack`. Seit das Handbuch ein
     * Reiter der SiGe-Plan-Huelle ist, fuehrt `onBack` aber nur noch auf den
     * Plan-Reiter: die Hinweisknoepfe „Stammdaten"/„Verteiler" landeten damit
     * eine Ebene zu frueh. Fehlt die Prop, gilt weiter `onBack` — das Modul
     * verhaelt sich dann wie zuvor.
     */
    onHubSprung?: () => void;
    /**
     * Meldet dem KI-Assistenzfenster der Huelle, was dieses Modul beisteuert:
     * den Stand des Handbuchs und den Weg, einen Ordnungs-Vorschlag in den
     * ENTWURF zu uebernehmen. `null` beim Verlassen.
     */
    onKiBruecke?: (bruecke: KiBruecke | null) => void;
    isReadOnly?: boolean;
    /**
     * Das Projekt mit Leistungsstand und der Schreibweg — von der
     * SiGe-Plan-Huelle durchgereicht, fuer den Abschlussschritt nach der
     * Freigabe (Plan Paketposition B5). Fehlt eins, bucht das Handbuch nichts.
     */
    report?: Report | null;
    onUpdate?: (report: Report) => void;
}

type Reiter = 'uebersicht' | 'gewerke' | 'kapitel' | 'dokumente';

/** Welches der beiden Blaetter gedruckt werden soll. */
type DruckArt = 'handbuch' | 'aushang';

/** Farbe der Ablage-Statuszeile je Ton — eine Warnung (verwaiste Datei) ist kein Gruen. */
const ABLAGE_TON_KLASSE: Record<AblageTon, string> = {
    laeuft: 'text-slate-500',
    erfolg: 'text-emerald-700',
    warnung: 'text-amber-700',
    fehler: 'text-rose-700',
};

/**
 * Das SiGe-Handbuch — duenner Container.
 *
 * Er laedt die Lage, schaltet zwischen vier Reitern und ruft den Dienst. Jede
 * Regel, die man pruefen kann, steht daneben in `handbuchLogic.ts` (stehende
 * Entscheidung „duenner Container, Logik in Hooks und puren Funktionen"; es
 * gibt kein jsdom, Komponenten sind nicht testbar — siehe Kommentar in
 * PlanungModul.tsx). In dieser Datei steht deshalb KEINE Fachlogik: kein
 * Vollstaendigkeitsurteil, keine Fristrechnung, keine Seitenzaehlung.
 *
 * ═══ ZWEI REIHEN SCHREIBSCHUTZ ═════════════════════════════════════════════
 * `isReadOnly` schaltet die Knoepfe ab UND wird in jedem Schreibweg noch
 * einmal geprueft — hier ueber `abgelehntWennNurAnsicht` und im Dienst ueber
 * den `SchreibKontext`, den jeder schreibende Aufruf verlangt. Das ist kein
 * Guertel-und-Hosentraeger: das Modul haengt an eigenen Tabellen, hinter denen
 * kein `handleUpdateReport`-Wachposten steht, und `isReadOnly` kann zur
 * Laufzeit kippen, wenn die Projektsperre an einen anderen Benutzer geht.
 *
 * ═══ DIE KAPITEL SELBST FEHLEN HIER ABSICHTLICH ════════════════════════════
 * Der Reiter „Kapitel" zeigt den Seitenspiegel und den Platz, an dem die
 * Kapitelseiten entstehen (`kapitel/Deckblatt.tsx` und die uebrigen). Die
 * Grundbausteine dafuer stehen bereits: `A3Seite`, `SeitenKopf`, `SeitenFuss`,
 * `Abschnitt`, `StatusBadge`.
 */
export const HandbuchModul: React.FC<Props> = ({
    projectId, metadata, globalData, vorankuendigung, userProfile,
    onBack, onHubSprung, onNavigate, onKiBruecke, isReadOnly = false,
    report = null, onUpdate,
}) => {
    const {
        handbuch, setHandbuch, gewerke, nachweise, laden, fehler, neuLaden,
        planGewerke, planGeladen, planLaeuft, ladePlanGewerke, ergaenzeGewerke, ergaenzeNachweise,
        uebernehmeGewerk,
        kapitelQuelle, kapitelGeladen, kapitelLaeuft, ladeKapitelQuelle,
    } = useHandbuch(projectId);

    /**
     * ABSCHLUSSSCHRITT NACH DER FREIGABE (Plan Paketposition B5).
     *  - Fall 2 (Regelfall): das Handbuch ist Bestandteil einer Paketposition —
     *    die Ausgabe der Freigabe erledigt ihn per Trigger; der Hook meldet das
     *    und bucht ein dadurch vollstaendiges Paket. KEIN Auswahldialog.
     *  - Ohne Paket: Fall 1 nur ueber die Auswahl aus ALLEN Positionen (kein
     *    eigener Zuordnungswert), ohne Vorauswahl — wer bucht, waehlt aktiv.
     *  - Nur-Ansicht, Freelancer, ohne Schreibweg: kein Schritt.
     */
    const rechte = useRechte(userProfile?.role ?? null);
    const abschluss = useBestandteilAbschluss({ report, onUpdate, userProfile, quelle: 'handbuch', isReadOnly, projectId });
    /**
     * Nach einer Freigabe ohne Paket: das ANGEBOT „Leistung buchen" in der
     * Abschlussmeldung (Fassungsnummer fuer den Protokolltext). Kein
     * automatischer Dialog aus ALLEN Positionen nach jeder Freigabe — die
     * Auswahl oeffnet erst der Knopf (`buchungsDialog`).
     */
    const [buchungsAngebot, setBuchungsAngebot] = useState<{ fassungNr: string; projektId: string } | null>(null);
    const [buchungsDialog, setBuchungsDialog] = useState(false);
    const [buchungsMeldung, setBuchungsMeldung] = useState<{ ton: 'ok' | 'fehler'; text: string } | null>(null);
    const allePositionen = useMemo(
        () => alleBuchbarenPositionen(report?.metadata?.globalData?.services),
        [report?.metadata?.globalData?.services],
    );

    // Anhang-II-Nummern der Planungsphase fuer den Abgleich je Gewerk (nur
    // Hinweis, nie ankreuzen). Fail-open: ohne Planungsphase keine Hinweise.
    const planung = useProjektSteckbrief(projectId, metadata);
    const anhangIIPlanung = planung.steckbrief?.anhang2.nummern.wert ?? null;

    const projektName = metadata.projectName;
    const projektNummer = metadata.projectNumber;

    const [reiter, setReiter] = useState<Reiter>('uebersicht');
    const [meldung, setMeldung] = useState<string | null>(null);
    const [aktionsfehler, setAktionsfehler] = useState<string | null>(null);
    const [laeuft, setLaeuft] = useState(false);
    const [aenderungsvermerk, setAenderungsvermerk] = useState('');

    /**
     * Ein angeforderter Druck, der noch auf die Kapitelquellen wartet.
     *
     * Plan, Bewertungen, Ablage und Notrufplan werden erst beim Oeffnen des
     * Kapitel-Reiters geladen (acht Abfragen). Wer aus der Uebersicht heraus
     * druckt, bekaeme sonst ein Handbuch mit leeren Kapiteln — und ein leeres
     * Kapitel liest sich wie „da ist nichts", nicht wie „nicht nachgesehen".
     * Der Wunsch wird deshalb gemerkt, die Quellen werden nachgeladen, und
     * gedruckt wird, sobald sie da sind.
     */
    const [druckWunsch, setDruckWunsch] = useState<DruckArt | null>(null);
    /**
     * Eine Fassung, die auf die Kapitelquellen wartet, bevor sie in der
     * zentralen Ablage landet (Stufe 1A) — dasselbe Muster wie `druckWunsch`:
     * die Zweitschrift braucht dieselben Quellen wie der Druck, sonst laege
     * in der Ablage ein Handbuch mit leeren Kapiteln.
     */
    const [ablageWunsch, setAblageWunsch] = useState<HandbuchFassung | null>(null);
    /**
     * Der letzte Ablageversuch. `auftrag` ist der GESENDETE Auftrag (Bytes,
     * Kennung, Titel): „Erneut ablegen" schickt genau ihn noch einmal, statt
     * aus dem inzwischen vielleicht geaenderten Handbuch ein neues Blatt unter
     * der alten Kennung zu erzeugen. `null`, wenn es nie bis zum Auftrag kam.
     */
    const [ablage, setAblage] = useState<{
        fassung: HandbuchFassung;
        stand: AblageStand;
        auftrag: HandbuchAblageAuftrag | null;
        /** Anmerkungen des Kerns (Journal-Eintrag, Steckbrief) — kein Fehler, dezent gezeigt. */
        hinweise?: string[];
    } | null>(null);
    /**
     * Der Planungsstand der letzten Freigabe konnte nicht festgehalten werden
     * (Stufe 6.1, `halteFreigabeFest`). Die Freigabe gilt trotzdem; der Satz
     * bleibt sichtbar, bis die naechste Freigabe ihn ersetzt. `wiederholung`
     * ist der Auftrag fuer „Erneut festhalten" mit GENAU dem Anker des ersten
     * Versuchs — `null`, wenn keiner entstand (dann nur der Hinweis).
     */
    const [freigabeJournal, setFreigabeJournal] = useState<{ hinweis: string; wiederholung: FreigabeFesthalten | null } | null>(null);
    /**
     * Auslöser fuer den Aenderungsdienst (`AbweichungsHinweis`): nach
     * Freigabe-Journal, „Erneut festhalten" und „Neue Fassung eroeffnen"
     * hochgezaehlt, damit der Hinweis die NEUE Fassung vergleicht — nicht die
     * alte, deren Entscheid die frische Freigabe gleich wieder eroeffnete.
     */
    const [abgleichLauf, setAbgleichLauf] = useState(0);
    const abgleichNeu = () => setAbgleichLauf(n => n + 1);
    /** A3 ist der Standard; A2 fuer grosse Baustellen. Kein A4 — der Aushang
     *  soll aus drei Metern lesbar sein. */
    const [aushangFormat, setAushangFormat] = useState<AushangFormat>('a3');

    /**
     * Der Entwurf des Kopfsatzes — Baustellenordnung und Baustelleneinrichtung.
     *
     * ═══ WARUM EIN ENTWURF UND NICHT SOFORT SCHREIBEN ══════════════════════
     * Beide Erfassungen sind Textfelder, und ihr `onChange` feuert je
     * Tastendruck. Ohne diesen Zwischenspeicher waere jeder Buchstabe ein
     * `update` auf `handbuecher` — auf einer schmalen Baustellenleitung
     * unbrauchbar, und jede zweite Antwort ueberholte die vorige. Gespeichert
     * wird auf Knopfdruck; bis dahin sehen die Kapitelseiten den Entwurf, damit
     * Erfassung und Vorschau nicht auseinanderlaufen.
     *
     * `null` heisst „nichts Offenes". Das ist etwas anderes als ein Entwurf,
     * der zufaellig gleich aussieht: nur beim `null` verschwindet der Balken.
     */
    const [ordnungEntwurf, setOrdnungEntwurf] = useState<OrdnungDaten | null>(null);
    const [einrichtungEntwurf, setEinrichtungEntwurf] = useState<EinrichtungDaten | null>(null);

    /**
     * Was am offenen Entwurf aus einem KI-Vorschlag stammt.
     *
     * ═══ WARUM DIE HERKUNFT NEBEN DEN DATEN LIEGT ══════════════════════════
     * `Baustellenordnung` hat keine Herkunftsspalte, und ein Vermerk IM TEXT
     * („(KI)") stuende spaeter auf dem Aushang am Bauzaun. Der Nachweis gehoert
     * aber dorthin, wo er gebraucht wird: in den Balken ueber dem Entwurf,
     * BEVOR gespeichert wird. Danach ist es der Text des Koordinators — er hat
     * ihn gelesen und freigegeben, und genau das ist die Aussage der
     * Baustellenordnung.
     */
    const [kiHerkunft, setKiHerkunft] = useState<OrdnungHerkunft | null>(null);

    /**
     * Dasselbe fuer die Baustelleneinrichtung — und ausdruecklich ein zweiter
     * Zustand.
     *
     * Ordnung und Einrichtung werden EINZELN uebernommen; ein gemeinsamer
     * Nachweis behauptete nach einer Ordnungs-Uebernahme, auch die Einrichtung
     * stamme aus der KI. Der Balken ist der einzige Ort, an dem ein
     * Gegenleser das erfaehrt, und dort darf nichts Falsches stehen.
     */
    const [kiEinrichtungHerkunft, setKiEinrichtungHerkunft] =
        useState<EinrichtungHerkunft | null>(null);

    /**
     * Der offene Vorschlag aus dem REGELWERK (ohne KI), samt Auswahl.
     *
     * Er wird angezeigt und nicht eingeschrieben: `null` heisst „kein
     * Vorschlag offen". Die Auswahl haelt die Zeilen, die der Anwender
     * angehakt gelassen hat — vorbelegt sind alle.
     */
    const [vorschlag, setVorschlag] = useState<EinrichtungsVorschlag | null>(null);
    const [vorschlagAuswahl, setVorschlagAuswahl] = useState<string[]>([]);
    const [vorschlagLaeuft, setVorschlagLaeuft] = useState(false);

    // Doppelklick-Riegel. `laeuft` als Zustandsvariable greift erst nach dem
    // naechsten Zeichnen — ein schneller zweiter Klick startet sonst einen
    // zweiten Lauf. Muster aus EmailModal, EinweisungModul und PlanungModul.
    const laufRef = useRef(false);

    /**
     * Der heutige Tag — ORTSZEIT.
     *
     * `toISOString()` rechnet nach UTC um: um 00:30 MESZ am 23.08. liefert es
     * den 22.08. Genau diesen Tag Unterschied entscheidet, ob ein Nachweis
     * „überfällig" oder nur „bald fällig" ist — und im zweiten Fall geht die
     * Freigabe durch. Deshalb dieselbe Funktion, mit der das ganze Repo seine
     * Kalendertage bildet.
     */
    const heuteIso = useMemo(() => formatDateLocal(new Date()), []);
    const gesperrt = istGesperrt(handbuch);

    const autorName = useMemo(() => (
        [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ').trim()
        || userProfile?.full_name || userProfile?.name || ''
    ), [userProfile]);

    /** Der Kontext, den JEDER schreibende Dienstaufruf verlangt. Er wird aus
     *  dem gerade geltenden Zustand gebaut und nicht gemerkt — sonst schriebe
     *  ein alter Kontext an der inzwischen erfolgten Freigabe vorbei. */
    const kontext = useCallback((): SchreibKontext => ({
        nurAnsicht: isReadOnly,
        status: handbuch?.status,
    }), [isReadOnly, handbuch?.status]);

    const befunde = useMemo(
        () => kapitelBefunde({ handbuch, gewerke, nachweise }, heuteIso, planGewerke),
        [handbuch, gewerke, nachweise, heuteIso, planGewerke],
    );
    const quote = useMemo(() => vollstaendigkeitsQuote(befunde), [befunde]);
    const freigabe = useMemo(
        () => pruefeFreigabe({ handbuch, gewerke, nachweise }, heuteIso, planGewerke),
        [handbuch, gewerke, nachweise, heuteIso, planGewerke],
    );
    const nachweisLage = useMemo(() => nachweisUebersicht(nachweise, heuteIso), [nachweise, heuteIso]);
    const seiten = useMemo(() => seitenPlan({ handbuch, gewerke, nachweise }), [handbuch, gewerke, nachweise]);
    const abgleich = useMemo(() => abgleicheGewerke(planGewerke, gewerke), [planGewerke, gewerke]);

    const offenerEntwurf = !!ordnungEntwurf || !!einrichtungEntwurf;

    /**
     * Der Handbuchsatz, wie ihn die Kapitel sehen: mit den noch nicht
     * gespeicherten Feldern. So zeigt die Vorschau, was gedruckt wuerde, wenn
     * jetzt gespeichert wird — und nicht den Stand von vorhin.
     */
    const handbuchAnsicht = useMemo<Handbuch | null>(() => {
        if (!handbuch) return null;
        if (!offenerEntwurf) return handbuch;
        return {
            ...handbuch,
            ordnung: ordnungEntwurf ?? handbuch.ordnung,
            einrichtung: einrichtungEntwurf ?? handbuch.einrichtung,
        };
    }, [handbuch, offenerEntwurf, ordnungEntwurf, einrichtungEntwurf]);

    /** Die Einrichtungsfelder im Formular: Entwurf, sonst Bestand, sonst leer. */
    const einrichtung = einrichtungEntwurf ?? handbuch?.einrichtung ?? leereEinrichtung();

    /** Zwei Reihen Schreibschutz — die Knoepfe sind aus, UND jeder Schreibweg
     *  prueft noch einmal (hier `abgelehntWennNurAnsicht`, im Dienst der
     *  `SchreibKontext`). */
    const darfAendern = !!handbuch && !isReadOnly && !gesperrt;

    const planNr = kapitelQuelle.sigeProject?.plan_nr;
    const planVersion = kapitelQuelle.sigeProject?.version_label || undefined;

    // Der 30-Minuten-Leerlaufrueckzug darf das Projekt nicht schliessen,
    // solange ein Aenderungsvermerk halb getippt im Feld steht — oder ein
    // ungespeicherter Entwurf von Ordnung/Einrichtung offen ist.
    useEffect(() => {
        if (!aenderungsvermerk.trim() && !offenerEntwurf) return;
        return holdIdleExit('handbuch');
    }, [aenderungsvermerk, offenerEntwurf]);

    // Die Kapitelquellen kommen erst, wenn jemand den Kapitel-Reiter oeffnet:
    // es sind acht Abfragen, und wer nur eine Gewerkeseite anlegt, braucht
    // keine davon. Ein angeforderter Druck oder eine wartende Ablage holt sie
    // ebenfalls — beide brauchen dieselben Quellen wie die Vorschau.
    useEffect(() => {
        if (kapitelGeladen || kapitelLaeuft) return;
        if (reiter !== 'kapitel' && !druckWunsch && !ablageWunsch) return;
        void ladeKapitelQuelle();
    }, [reiter, druckWunsch, ablageWunsch, kapitelGeladen, kapitelLaeuft, ladeKapitelQuelle]);

    /** Zweite Reihe des Schreibschutzes im Container: greift auch dann, wenn
     *  ein Knopf wider Erwarten aktiv ist oder `isReadOnly` zur Laufzeit
     *  kippt. Die dritte Reihe steht im Dienst. */
    const abgelehntWennNurAnsicht = (was: string): boolean => {
        if (!isReadOnly) return false;
        setAktionsfehler(readOnlyMessage(was));
        return true;
    };

    /** `useCallback`, damit der Druck-Effekt sie in seine Abhaengigkeiten
     *  aufnehmen kann, ohne bei jedem Zeichnen neu zu laufen. */
    const melde = useCallback((text: string) => {
        setMeldung(text);
        setAktionsfehler(null);
        window.setTimeout(() => setMeldung(m => (m === text ? null : m)), 6000);
    }, []);

    // ─── Druck ───────────────────────────────────────────────────────────

    /**
     * Alles, was der Handbuchgenerator braucht — an EINER Stelle.
     *
     * Gedruckt wird `handbuchAnsicht` und nicht `handbuch`: der offene Entwurf
     * von Ordnung und Einrichtung ist das, was die Kapitelvorschau zeigt, und
     * Papier und Bildschirm duerfen nicht auseinanderlaufen.
     */
    const druckDaten = useMemo<HandbuchPdfDaten>(() => ({
        lage: { handbuch: handbuchAnsicht, gewerke, nachweise },
        projekt: {
            ...projektAngabenAus(metadata, globalData, { planNr, planVersion }),
            // Die Firma des Koordinators steht nicht in `metadata` — sie ist
            // die eigene Firma und kommt aus dem Profil. `handbuchIntegration`
            // raet sie ausdruecklich nicht; der Aufrufer reicht sie nach.
            koordinatorFirma: userProfile?.company_name || '',
        },
        planGewerke,
        schritte: kapitelQuelle.arbeitsschritte,
        gefaehrdungen: gefaehrdungenJeSchritt(kapitelQuelle.gefaehrdungen),
        massnahmen: massnahmenJeGefaehrdung(kapitelQuelle.massnahmen),
        notrufplan: kapitelQuelle.notrufplan,
        paare: kapitelQuelle.paare,
        dokumente: kapitelQuelle.dokumente,
        planFassungen: kapitelQuelle.planFassungen,
        // „Nicht nachgesehen" und „gibt es nicht" sehen auf dem Blatt gleich
        // aus und meinen Gegenteiliges.
        planUngeladen: !kapitelGeladen,
        heuteIso,
    }), [
        handbuchAnsicht, gewerke, nachweise, metadata, globalData, planNr, planVersion,
        userProfile, planGewerke, kapitelQuelle, kapitelGeladen, heuteIso,
    ]);

    const aushangDaten = useMemo<AushangDaten>(() => ({
        ...aushangDatenAus(metadata, handbuchAnsicht, kapitelQuelle.notrufplan, {
            planNr,
            // Der Stand des AUSHANGS ist der Tag, an dem er gedruckt wurde —
            // ein Blatt am Bauzaun soll sagen, wie alt es ist. Der Stand des
            // Handbuchs (`handbuecher.stand`) ist ein Monat und kein Tag.
            standIso: heuteIso,
        }),
        koordinatorFirma: userProfile?.company_name || '',
    }), [metadata, handbuchAnsicht, kapitelQuelle.notrufplan, planNr, heuteIso, userProfile]);

    const fassung = handbuch?.version_label || null;

    /**
     * Erzeugt und speichert das Blatt.
     *
     * Fehler werden GEFANGEN und benannt statt das Modul weisszubrennen:
     * `handbuchIntegration` gibt sie als `{ status: 'fehler' }` zurueck —
     * dasselbe Muster wie in `planungIntegration` und `einweisungIntegration`.
     */
    /**
     * Das Handbuch-Blatt — EIN Weg fuer Druck und Ablage, damit die abgelegte
     * Zweitschrift dem gedruckten Blatt gleicht.
     *
     * Der QR-Code der Online-Einweisung wird erst hier geladen: er zeigt auf
     * die juengste Einweisung mit aktivem Link, und die kann sich zwischen
     * zwei Drucken aendern. Kein Link (oder ein Fehler beim Laden) ergibt
     * null — dann steht kein Block auf dem Deckblatt.
     */
    const baueHandbuchBlatt = useCallback(async (daten: HandbuchPdfDaten) => {
        const einweisungQr = projectId && projectId !== 'temp-id'
            ? await ladeEinweisungQrFuerProjekt(projectId, window.location.origin, window.location.pathname)
            : null;
        return baueHandbuchPdf({ ...daten, einweisungQr });
    }, [projectId]);

    const fuehreDruckAus = useCallback(async (art: DruckArt) => {
        const gebaut = art === 'handbuch'
            ? await baueHandbuchBlatt(druckDaten)
            : baueAushangPdf(aushangDaten, aushangFormat);
        if (gebaut.status === 'fehler') { setAktionsfehler(gebaut.fehler); return; }

        const name = art === 'handbuch'
            ? handbuchDateiname(projektName || '', fassung, heuteIso)
            : aushangDateiname(projektName || '', aushangFormat, heuteIso);
        const gespeichert = speicherePdf(gebaut.wert, name);
        if (gespeichert.status === 'fehler') { setAktionsfehler(gespeichert.fehler); return; }

        melde(art === 'handbuch'
            ? `Das SiGe-Handbuch wurde als ${name} erzeugt.`
            : `Der Aushang (${aushangFormat.toUpperCase()}) wurde als ${name} erzeugt.`);
    }, [baueHandbuchBlatt, druckDaten, aushangDaten, aushangFormat, projektName, fassung, heuteIso, melde]);

    const drucke = (art: DruckArt) => {
        if (!handbuch) return;
        if (!kapitelGeladen) {
            // Der Effekt weiter unten laedt nach und druckt danach.
            setDruckWunsch(art);
            melde('Plan, Bewertungen und Ablage werden geladen — der Druck startet gleich.');
            return;
        }
        void fuehreDruckAus(art);
    };

    // Der nachgereichte Druck. Er laeuft genau einmal: `setDruckWunsch(null)`
    // steht VOR dem Erzeugen, damit ein zweiter Lauf gar nicht erst entsteht.
    useEffect(() => {
        if (!druckWunsch || !kapitelGeladen) return;
        setDruckWunsch(null);
        void fuehreDruckAus(druckWunsch);
    }, [druckWunsch, kapitelGeladen, fuehreDruckAus]);

    // ─── Zentrale Ablage (Stufe 1A) ──────────────────────────────────────

    /**
     * Legt eine Fassung des Handbuchs in der zentralen Ablage ab.
     *
     * Gedruckt wird der GESPEICHERTE Stand (`handbuch`, nicht
     * `handbuchAnsicht`): ein offener Entwurf von Ordnung/Einrichtung ist noch
     * nicht Teil der Fassung — bei der Freigabe schon gar nicht, denn
     * eingefroren wurde, was in der Datenbank steht. Fehler werden benannt,
     * nie geworfen; Freigabe und Druck haengen nicht an diesem Weg.
     */
    /** Schickt einen FERTIGEN Auftrag — der erste Versuch und jedes „Erneut
     *  ablegen" laufen hier durch, mit denselben Bytes und derselben Kennung.
     *  Der Steckbrief-Anker haengt nicht an diesem Versuch: er wurde bei der
     *  Freigabe festgehalten (`gibFrei`), die Ablage bildet keinen. */
    const sendeAblageAuftrag = useCallback(async (fassung: HandbuchFassung, auftrag: HandbuchAblageAuftrag) => {
        setAblage({ fassung, stand: { art: 'laeuft' }, auftrag });
        const freigeben = holdIdleExit('handbuch-ablage');
        try {
            const ergebnis = await legeUnterlageAb(auftrag);
            setAblage({ fassung, stand: ablageStandAus(ergebnis, auftrag.titel), auftrag, hinweise: ablageHinweise(ergebnis) });
        } catch (e) {
            // `legeUnterlageAb` verspricht, nie zu werfen. Faellt doch etwas
            // durch, bleibt es eine Meldung — und der Auftrag bleibt festgehalten.
            setAblage({ fassung, stand: ablageFehler(e instanceof Error ? e.message : String(e)), auftrag });
        } finally {
            freigeben();
        }
    }, []);

    const fuehreAblageAus = useCallback(async (gewuenscht: HandbuchFassung) => {
        // Der Entwurfsstand wird JETZT gebildet — mit den geladenen
        // Kapitelquellen, die ebenfalls auf dem Blatt stehen. Eine Freigabe
        // behaelt ihre Kennung (Nummer + Zeitpunkt).
        const lage = { handbuch, gewerke, nachweise };
        const fassung = mitInhaltsStand(gewuenscht, lage, kapitelQuelle);
        setAblage({ fassung, stand: { art: 'laeuft' }, auftrag: null });
        const freigeben = holdIdleExit('handbuch-ablage');
        try {
            const gebaut = await baueHandbuchBlatt({ ...druckDaten, lage });
            if (gebaut.status === 'fehler') { setAblage({ fassung, stand: ablageFehler(gebaut.fehler), auftrag: null }); return; }
            const bytes = pdfAlsBlob(gebaut.wert);
            if (bytes.status === 'fehler') { setAblage({ fassung, stand: ablageFehler(bytes.fehler), auftrag: null }); return; }
            // Groesse VOR dem Upload — die Ablage nimmt hoechstens 50 MB.
            const groesse = pruefeAblageGroesse(bytes.wert.size);
            if (groesse.status === 'fehler') { setAblage({ fassung, stand: ablageFehler(groesse.grund), auftrag: null }); return; }
            // Die Stammdaten gehen mit: bei der FREIGABE legt der Kern dazu den
            // Journal-Eintrag des Aenderungsdienstes an (Stufe 6.1); ein Entwurf
            // bekommt keinen (`handbuchAusgabe`).
            await sendeAblageAuftrag(fassung, baueHandbuchAblageAuftrag(projectId, bytes.wert, fassung, projektName || '', metadata));
        } catch (e) {
            // Die Generatoren sind schon gefangen; faellt doch etwas durch,
            // bleibt es eine Meldung.
            setAblage({ fassung, stand: ablageFehler(e instanceof Error ? e.message : String(e)), auftrag: null });
        } finally {
            freigeben();
        }
    }, [baueHandbuchBlatt, druckDaten, handbuch, gewerke, nachweise, kapitelQuelle, projectId, projektName, metadata, sendeAblageAuftrag]);

    // Die nachgereichte Ablage — genau einmal, `setAblageWunsch(null)` steht
    // VOR dem Lauf (wie beim Druck). Bei der Freigabe wartet sie zusaetzlich,
    // bis der eingefrorene Stand im Zustand angekommen ist — sonst truege die
    // Zweitschrift „Entwurf" statt der Fassungsnummer.
    useEffect(() => {
        if (!ablageWunsch || !kapitelGeladen) return;
        if (ablageWunsch.anlass === 'freigabe' && handbuch?.status !== 'freigegeben') return;
        setAblageWunsch(null);
        void fuehreAblageAus(ablageWunsch);
    }, [ablageWunsch, kapitelGeladen, handbuch?.status, fuehreAblageAus]);

    /**
     * Knopf „Fassung/Entwurf in Dokumente ablegen" und „Erneut ablegen".
     *
     * Ein eingefrorenes Handbuch legt seine GELTENDE Fassung ab — mit derselben
     * Kennung wie die automatische Ablage bei der Freigabe, damit die Ablage
     * einen zweiten Versuch als denselben Stand erkennt. Ein Entwurf wird
     * deutlich als Entwurf beschriftet (E17: Entwuerfe nur per Knopf).
     */
    const legeAbVonHand = (abzulegen: HandbuchFassung | null) => {
        if (!abzulegen) return;
        if (abgelehntWennNurAnsicht('das Handbuch in Dokumente abzulegen')) return;
        const frei = sollHandbuchAblegen({ projectId, isReadOnly });
        if (frei.status === 'fehler') { setAblage({ fassung: abzulegen, stand: ablageFehler(frei.grund), auftrag: null }); return; }
        setAblageWunsch(abzulegen);
    };
    const legeAktuellenStandAb = () => {
        if (!handbuch) return;
        legeAbVonHand(gesperrt
            ? fassungDerGeltendenFreigabe(handbuch)
            : entwurfsFassung({ handbuch, gewerke, nachweise }, new Date().toISOString()));
    };
    const ablageLaeuft = !!ablageWunsch || ablage?.stand.art === 'laeuft';

    /**
     * „Erneut ablegen": der festgehaltene Auftrag geht unveraendert noch
     * einmal (dieselben Bytes, dieselbe Kennung). Gibt es keinen — der erste
     * Versuch scheiterte schon am PDF oder an der Groesse —, wird neu
     * abgelegt, und zwar mit der Fassung des HEUTIGEN Standes, nie mit der
     * alten Kennung (`ablageWiederholung`).
     */
    const erneutAblegen = () => {
        if (!ablage) return;
        if (abgelehntWennNurAnsicht('das Handbuch in Dokumente abzulegen')) return;
        const frei = sollHandbuchAblegen({ projectId, isReadOnly });
        if (frei.status === 'fehler') { setAblage({ ...ablage, stand: ablageFehler(frei.grund) }); return; }
        const wie = ablageWiederholung(ablage);
        if (wie.art === 'auftrag') { void sendeAblageAuftrag(ablage.fassung, wie.auftrag); return; }
        legeAktuellenStandAb();
    };

    const mitLauf = async (fn: () => Promise<void>) => {
        if (laufRef.current) return;
        laufRef.current = true;
        setLaeuft(true);
        setAktionsfehler(null);
        const freigeben = holdIdleExit('handbuch-aktion');
        try {
            await fn();
        } catch (e) {
            setAktionsfehler(e instanceof Error ? e.message : String(e));
        } finally {
            freigeben();
            laufRef.current = false;
            setLaeuft(false);
        }
    };

    // ─── Anlegen ─────────────────────────────────────────────────────────

    const legeAn = () => {
        if (abgelehntWennNurAnsicht('ein SiGe-Handbuch anzulegen')) return;
        void mitLauf(async () => {
            const neu = await ensureHandbuch(kontext(), projectId);
            setHandbuch(neu);
            melde('Das Handbuch wurde angelegt.');
        });
    };

    const setzeOrgModell = (modell: OrgModell) => {
        if (!handbuch) return;
        if (abgelehntWennNurAnsicht('das Vergabemodell zu setzen')) return;
        void mitLauf(async () => {
            setHandbuch(await updateHandbuch(kontext(), handbuch.id, { org_modell: modell }));
            melde(`Vergabemodell: ${ORG_MODELLE[modell].label}.`);
        });
    };

    // ─── Gewerke aus dem Plan ────────────────────────────────────────────

    const holePlan = () => {
        void mitLauf(async () => {
            const liste = await ladePlanGewerke();
            melde(liste.length
                ? `${liste.length} Gewerk(e) im Plan gefunden.`
                : 'Der Plan enthält keine Arbeitsschritte mit Gewerk.');
        });
    };

    /**
     * Zieht die noch fehlenden Gewerkeseiten nach.
     *
     * Der Nachweisblock wird je Seite EINZELN geschrieben, weil er den
     * Fremdschluessel auf die soeben entstandene Seite braucht. Ein Fehler in
     * der Mitte laesst also einige Seiten mit und einige ohne Nachweise
     * zurueck — das ist unschoen, aber unschaedlich: ein erneuter Klick legt
     * die fehlenden Seiten nicht doppelt an (Unique-Index), und fehlende
     * Nachweise ergaenzt man je Seite.
     */
    const zieheNach = () => {
        if (!handbuch) return;
        if (abgelehntWennNurAnsicht('Gewerkeseiten anzulegen')) return;
        void mitLauf(async () => {
            const offen = abgleich.neu;
            if (!offen.length) { melde('Alle Gewerke des Plans haben bereits eine Seite.'); return; }

            const start = gewerke.length;
            const angelegt = await createHandbuchGewerke(
                kontext(), handbuch.id, projectId,
                offen.map((g, i) => entwurfAusGewerk(g, start + i)),
            );
            ergaenzeGewerke(angelegt);

            for (const seite of angelegt) {
                const quelle = offen.find(g => g.gewerk_norm === seite.gewerk_norm);
                const entwuerfe = nachweisEntwuerfe(quelle?.katalogId ?? null);
                if (!entwuerfe.length) continue;
                ergaenzeNachweise(
                    await createHandbuchNachweise(kontext(), projectId, seite.id, entwuerfe),
                );
            }
            melde(`${angelegt.length} Gewerkeseite(n) angelegt.`);
        });
    };

    // ─── Kopfsatz: Baustellenordnung und Baustelleneinrichtung ───────────

    /**
     * Speichert den offenen Entwurf.
     *
     * Geschrieben werden NUR die Felder, an denen etwas offen ist. Wer beide
     * mitschickte, ueberschriebe eine Ordnung, die inzwischen jemand anderes
     * geaendert hat, mit dem Stand von vorhin — und zwar unbemerkt.
     */
    const speichereKopfsatz = () => {
        if (!handbuch || !offenerEntwurf) return;
        if (abgelehntWennNurAnsicht('die Angaben zu speichern')) return;
        void mitLauf(async () => {
            const felder: Partial<Pick<Handbuch, 'ordnung' | 'einrichtung'>> = {};
            if (ordnungEntwurf) felder.ordnung = ordnungEntwurf;
            if (einrichtungEntwurf) felder.einrichtung = einrichtungEntwurf;
            setHandbuch(await updateHandbuch(kontext(), handbuch.id, felder));
            setOrdnungEntwurf(null);
            setEinrichtungEntwurf(null);
            setKiHerkunft(null);
            setKiEinrichtungHerkunft(null);
            melde('Die Angaben wurden gespeichert.');
        });
    };

    const verwirfKopfsatz = () => {
        setOrdnungEntwurf(null);
        setEinrichtungEntwurf(null);
        setKiHerkunft(null);
        setKiEinrichtungHerkunft(null);
    };

    /** Ein einzelnes Feld der Baustelleneinrichtung im Entwurf setzen. */
    const setzeEinrichtung = (feld: keyof EinrichtungDaten, wert: string) => {
        if (!darfAendern) return;
        setEinrichtungEntwurf(alt => ({ ...(alt ?? einrichtung), [feld]: wert }));
    };

    /** Einen Textbaustein an das Feld anhaengen. Zweimal klicken verdoppelt
     *  nichts — das entscheidet `fuegeBausteinEin`. */
    const klickeBaustein = (baustein: Baustein) => {
        if (!darfAendern) return;
        setzeEinrichtung(baustein.feld, fuegeBausteinEin(einrichtung[baustein.feld], baustein));
    };

    /**
     * Erzeugt den Vorschlag aus den Projektdaten.
     *
     * Holt die Planquellen NACH, wenn sie noch nicht geladen sind: der
     * Uebersichts-Reiter laedt sie nicht von allein (acht Abfragen, die
     * niemand braucht, der nur eine Gewerkeseite anlegt). Ohne das
     * Nachladen beruhte der Vorschlag auf einer leeren Liste — und
     * behauptete anschliessend, das Projekt habe keine Gewerke.
     */
    const erzeugeVorschlag = () => {
        if (vorschlagLaeuft) return;
        setVorschlagLaeuft(true);
        setAktionsfehler(null);
        void (async () => {
            try {
                const quelle = kapitelGeladen ? kapitelQuelle : await ladeKapitelQuelle();
                const ausPlan = planGeladen ? planGewerke : await ladePlanGewerke();
                const neuer = einrichtungsVorschlag({
                    gewerke: ausPlan.map(g => ({ katalogId: g.katalogId, label: g.gewerk_label })),
                    einrichtungen: gemeinsameEinrichtungen(quelle.arbeitsschritte),
                    sammelplatz: handbuchAnsicht?.ordnung?.sammelplatz,
                    lageplanVorhanden: (metadata.plans || []).length > 0,
                    quellenGeladen: true,
                });
                setVorschlag(neuer);
                setVorschlagAuswahl(neuer.zeilen.map(z => z.text));
            } catch (e) {
                setAktionsfehler(e instanceof Error ? e.message : String(e));
            } finally {
                setVorschlagLaeuft(false);
            }
        })();
    };

    /** Die angehakten Zeilen in den ENTWURF uebernehmen. Gespeichert wird
     *  erst ueber den blauen Balken — wie bei jedem anderen Vorschlag auch. */
    const uebernimmVorschlag = () => {
        if (!vorschlag || !darfAendern) return;
        const gewaehlt = vorschlag.zeilen.filter(z => vorschlagAuswahl.includes(z.text));
        if (!gewaehlt.length) return;
        setEinrichtungEntwurf(wendeVorschlagAn(einrichtung, { ...vorschlag, zeilen: gewaehlt }));
        setVorschlag(null);
        melde(`${gewaehlt.length} Satz/Sätze in den Entwurf übernommen. Gespeichert ist noch `
            + 'nichts — der blaue Balken oben tut das.');
    };

    // ─── Sprungziele ─────────────────────────────────────────────────────

    /**
     * Uebersetzt das fachliche Sprungziel eines Kapitels in eine Navigation.
     *
     * `stammdaten` und `verteiler` fuehren ZURUECK auf den Projekt-Hub: dort
     * stehen `ProjectMasterData` und „Projektbeteiligte & Verteiler". Ein
     * Modulschluessel dafuer existiert in `executeModuleNavigation` nicht, und
     * einen zu erfinden hiesse, die Navigation der Anwendung fuer dieses Modul
     * umzubauen. Der Weg dorthin ist `onHubSprung` und nicht `onBack`: seit das
     * Handbuch ein Reiter der SiGe-Plan-Huelle ist, fuehrt der Zurueck-Pfeil
     * nur noch auf den Plan-Reiter — die Hinweisknoepfe landeten damit eine
     * Ebene zu frueh.
     *
     * `lageplan` fuehrt ins Begehungsmodul: `metadata.plans` wird dort im
     * Abschnitt „Pläne" gepflegt. Feiner laesst es sich von aussen nicht
     * ansteuern — `executeModuleNavigation` kennt nur ganze Module, und der
     * Abschnitt ist der erste, den man dort sieht.
     *
     * `notrufplan` fuehrt in den SiGe-Plan: der Notrufplan ist ein Block
     * dieses Plans und wird ausdruecklich NICHT ein zweites Mal erfasst —
     * sonst stuenden zwei Krankenhaeuser im selben Projekt.
     */
    const springe = (ziel: SprungZiel) => {
        if (ziel === 'handbuch') { setReiter('uebersicht'); return; }
        if (ziel === 'stammdaten' || ziel === 'verteiler') { (onHubSprung || onBack)(); return; }
        if (!onNavigate) return;
        if (ziel === 'vorankuendigung') { onNavigate('vorank'); return; }
        if (ziel === 'lageplan') { onNavigate('editor'); return; }
        onNavigate('sigeplan');   // 'sigeplan' und 'notrufplan'
    };

    // ─── KI-Assistent ────────────────────────────────────────────────────

    /**
     * Die Innereien der Bruecke zum Assistenzfenster, nach jedem Zeichnen frisch.
     *
     * Die Bruecke selbst bleibt dieselbe (siehe `kiBruecke`), sonst meldete sie
     * sich bei der Huelle in einer Endlosschleife neu an. Ihr INHALT darf
     * dagegen nicht einfrieren: sonst arbeitete der Assistent mit dem Stand von
     * vorhin und uebersaehe eine Freigabe, die inzwischen erteilt wurde.
     */
    const kiStand = useRef<{
        oeffne: () => Promise<KiOeffnung>;
        uebernimm: (auswahl: KiUebernahme) => Promise<string>;
    } | null>(null);

    useEffect(() => {
        // Gewerkeseiten ohne einen einzigen Nachweis — die auffaelligste
        // Fehlstelle, und sie steht in keiner Kennzahl.
        const mitNachweis = new Set(nachweise.map(n => n.handbuch_gewerk_id));
        const ohneNachweise = gewerke.filter(g => !mitNachweis.has(g.id)).map(g => g.gewerk_label);

        const sperrgrund = !handbuch
            ? 'Für dieses Projekt gibt es noch kein Handbuch. Fragen geht, übernehmen erst danach.'
            : isReadOnly
                ? readOnlyTitle('Vorschläge des Assistenten in den Entwurf übernehmen')
                : gesperrt
                    ? sperrHinweis(handbuch)
                    : null;

        kiStand.current = {
            oeffne: async () => {
                // Ein halb geladener Stand liest sich wie ein leeres Handbuch —
                // die KI schluege dann vor, was laengst dasteht.
                if (laden) {
                    throw new Error('Der Stand wird noch geladen. Bitte gleich noch einmal öffnen.');
                }
                // Die Plandaten kommen sonst erst beim Oeffnen des
                // Kapitel-Reiters. Ohne sie kennte der Assistent weder die
                // Gewerke noch die gemeinsam genutzten Einrichtungen dieses
                // Projekts — sein Vorschlag zur Baustelleneinrichtung waere ein
                // Textbaustein und keine Aussage ueber diese Baustelle. Das
                // Nachladen ist der Grund, warum `oeffne` asynchron ist.
                const quelle = kapitelGeladen ? kapitelQuelle : await ladeKapitelQuelle();
                const ausPlan = planGeladen ? planGewerke : await ladePlanGewerke();
                // Merkmale der Planungsphase (Bestand, Betrieb laeuft weiter,
                // Strassenbaustelle …) — ein Vorschlag zur Baustellenordnung,
                // der sie kennt, ist eine Aussage ueber DIESE Baustelle.
                // Fail-open: ohne Planungsphase `planung: null`.
                const planung = await ladePlanungsphaseFuerKi(projectId);
                return {
                    paket: {
                        art: 'handbuch',
                        // Der OFFENE Entwurf ist der Stand, den der Anwender sieht —
                        // der Assistent muss ihn kennen, sonst schlaegt er vor, was
                        // gerade ungespeichert im Formular steht.
                        bestand: handbuchAnsicht ? handbuchAnsicht.ordnung : null,
                        bestandEinrichtung: handbuchAnsicht ? handbuchAnsicht.einrichtung : null,
                        kontext: baueHandbuchKontext(
                            {
                                bauvorhaben: projektName,
                                bauzeitVon: globalData?.startDate,
                                bauzeitBis: globalData?.endDate,
                            },
                            {
                                orgModell: handbuch?.org_modell,
                                ordnung: handbuchAnsicht ? handbuchAnsicht.ordnung : null,
                                einrichtung: handbuchAnsicht ? handbuchAnsicht.einrichtung : null,
                                // Aus dem PLAN und nicht aus den Gewerkeseiten:
                                // der Plan ist die Quelle, aus der auch das
                                // Kapitel seine Einrichtungen ableitet. Zwei
                                // Listen liefen auseinander.
                                gewerke: ausPlan.map(g => g.gewerk_label),
                                gemeinsameEinrichtungen: gemeinsameEinrichtungen(quelle.arbeitsschritte)
                                    .map(e => e.bezeichnung),
                                lageplanVorhanden: (metadata.plans || []).length > 0,
                                unvollstaendigeKapitel: befunde
                                    .filter(b => !b.vollstaendig)
                                    .map(b => ({ kapitel: b.kapitel, titel: b.titel, fehlend: b.fehlend })),
                                gewerkeOhneNachweise: ohneNachweise,
                                nachweise: {
                                    gesamt: nachweisLage.gesamt,
                                    fehlt: nachweisLage.fehlt,
                                    ueberfaellig: nachweisLage.ueberfaellig,
                                    ohneBeleg: nachweisLage.ohneBeleg,
                                },
                            },
                            planung,
                        ),
                    },
                    darfUebernehmen: darfAendern,
                    sperrgrund,
                };
            },

            /**
             * Uebernimmt einen Ordnungs-Vorschlag in den ENTWURF.
             *
             * Ausdruecklich nicht in die Datenbank: der vorhandene Entwurfsweg
             * mit seinem sichtbaren Balken „Speichern/Verwerfen" ist genau die
             * Stelle, an der ein Mensch gegenliest. Ein Vorschlag, der still in
             * `handbuecher.ordnung` landete, stuende beim naechsten Druck auf
             * dem Aushang am Bauzaun, ohne dass ihn jemand gesehen hat.
             */
            uebernimm: async (auswahl: KiUebernahme) => {
                if (auswahl.art !== 'handbuch' && auswahl.art !== 'handbuch-einrichtung') {
                    throw new Error('Dieser Reiter übernimmt nur Angaben zum Handbuch.');
                }
                // Zweite Reihe: `isReadOnly` kann zur Laufzeit kippen, und
                // zwischen Oeffnen und Uebernehmen kann eine Freigabe liegen.
                if (isReadOnly) {
                    throw new Error(readOnlyMessage('einen KI-Vorschlag in den Entwurf zu übernehmen'));
                }
                if (!handbuch || gesperrt) {
                    throw new Error(sperrgrund || 'Das Handbuch nimmt gerade keine Änderungen an.');
                }
                // Zwei getrennte Entwuerfe, zwei getrennte Herkunftsnachweise:
                // wer die Baustellenordnung ergaenzen laesst, darf sich nicht
                // nebenbei die Baustelleneinrichtung veraendern.
                if (auswahl.art === 'handbuch-einrichtung') {
                    setEinrichtungEntwurf(auswahl.einrichtung);
                    setKiEinrichtungHerkunft(alt => vereineEinrichtungHerkunft(
                        alt || LEERE_EINRICHTUNG_HERKUNFT, auswahl.herkunft,
                    ));
                } else {
                    setOrdnungEntwurf(auswahl.ordnung);
                    setKiHerkunft(alt => vereineHerkunft(alt || LEERE_HERKUNFT, auswahl.herkunft));
                }
                const satz = `${auswahl.herkunft.anzahl} Punkt(e) in den Entwurf übernommen. `
                    + 'Gespeichert ist noch nichts — der blaue Balken oben tut das.';
                melde(satz);
                return satz;
            },
        };
    });

    /** Bleibt ueber die Lebensdauer des Moduls DIESELBE (siehe `kiStand`). */
    const kiBruecke = useMemo<KiBruecke>(() => ({
        oeffne: async () => {
            if (!kiStand.current) throw new Error('Der Stand ist noch nicht geladen.');
            return kiStand.current.oeffne();
        },
        uebernimm: async (auswahl) => {
            if (!kiStand.current) throw new Error('Der Stand ist noch nicht geladen.');
            return kiStand.current.uebernimm(auswahl);
        },
    }), []);

    useEffect(() => {
        onKiBruecke?.(kiBruecke);
        return () => onKiBruecke?.(null);
    }, [onKiBruecke, kiBruecke]);

    // ─── Freigabe ────────────────────────────────────────────────────────

    const gibFrei = () => {
        if (!handbuch) return;
        if (abgelehntWennNurAnsicht('das Handbuch freizugeben')) return;
        void mitLauf(async () => {
            const eintrag = freigabeEintrag(handbuch, autorName, heuteIso, aenderungsvermerk);
            const neu = await freigebenHandbuch(kontext(), handbuch, eintrag, new Date().toISOString());
            if (!neu) {
                // Kein Fehler, sondern ein Wettlauf: jemand anderes war schneller.
                await neuLaden();
                setAktionsfehler('Das Handbuch war bereits freigegeben. Der Stand wurde neu geladen.');
                return;
            }
            setHandbuch(neu);
            setAenderungsvermerk('');
            setBuchungsMeldung(null);
            melde(`Fassung ${eintrag.nr} freigegeben. Die Kapitel sind jetzt eingefroren.`);
            const abzulegen = fassungAusFreigabe(neu, eintrag);
            // Der Planungsstand DIESER Freigabe fuer den Aenderungsdienst —
            // jetzt, nicht erst bei der Ablage: die kann Tage spaeter
            // nachgeholt werden und friere dann den heutigen Stand fuer eine
            // alte Freigabe ein (Stufe 6.1). Scheitert das Journal, gilt die
            // Freigabe trotzdem; der Satz bleibt an der Freigabe sichtbar.
            const journal = await halteFreigabeJournalFest({
                projectId, unterlage: 'handbuch', fassungRefs: [handbuchFassungRef(abzulegen)], metadata,
            });
            // Abschlussschritt (Plan B5): Bestandteile nachfragen — mit den
            // Kennungen der Ausgaben dieser Freigabe. Ohne Paket das Angebot
            // „Leistung buchen" (Fall 1, Auswahl aus allen Positionen erst auf
            // Knopfdruck), mit Paket kein Auswahlweg.
            void abschluss.nachAblage(journal);
            setBuchungsDialog(false);
            // Das Projekt des Vorgangs wird HIER festgehalten — gebucht wird
            // spaeter nur in dieses (Codex R2b).
            setBuchungsAngebot(!abschluss.sperre && abschluss.pakete.length === 0 && allePositionen.length > 0
                ? { fassungNr: String(eintrag.nr), projektId: projektDesVorgangs(abschluss.holeBericht()) }
                : null);
            // Zweitschrift in die zentrale Ablage (Stufe 1A, E17) — NACH der
            // Freigabe und unabhaengig von ihr: scheitert sie, bleibt die
            // Freigabe gueltig, und die Fassung laesst sich erneut ablegen.
            const frei = sollHandbuchAblegen({ projectId, isReadOnly });
            if (frei.status === 'ok') setAblageWunsch(abzulegen);
            else setAblage({ fassung: abzulegen, stand: ablageFehler(frei.grund), auftrag: null });
        });
    };

    /**
     * Schreibt das Freigabe-Journal — erster Versuch und „Erneut festhalten"
     * gleich. Gibt das Ergebnis zurueck: seine Ausgaben-Kennungen braucht der
     * Abschlussschritt (Plan B5).
     */
    const halteFreigabeJournalFest = async (auftrag: FreigabeFesthalten): Promise<FreigabeJournal> => {
        try {
            const journal = await halteFreigabeFest(auftrag);
            setFreigabeJournal(journal.status === 'fehler'
                ? { hinweis: journal.hinweis || 'Der Planungsstand wurde nicht festgehalten.', wiederholung: freigabeWiederholung(auftrag, journal) }
                : null);
            return journal;
        } finally {
            // Der Aenderungsdienst soll die frische Fassung vergleichen.
            abgleichNeu();
        }
    };

    /** „Erneut festhalten": GENAU der Anker des ersten Versuchs, kein neuer Auszug. */
    const halteFreigabeErneutFest = () => {
        const w = freigabeJournal?.wiederholung;
        if (!w) return;
        if (abgelehntWennNurAnsicht('den Planungsstand der Freigabe festzuhalten')) return;
        void mitLauf(async () => {
            const journal = await halteFreigabeJournalFest(w);
            // Erst jetzt liegt die Ausgabe — und erst jetzt hat der Trigger den
            // Bestandteil erledigt (Plan B5).
            if (journal.status !== 'fehler') void abschluss.nachAblage(journal);
        });
    };

    /**
     * Fall 1 (Plan B5): die Buchung auf die gewaehlte Position — auf dem Stand,
     * der JETZT gilt (`holeBericht`), ueber `schreibe` (dieselbe Ref wie die
     * Paketbuchung), dann die Zweitschrift ins Buchungsjournal (Muster B).
     * „Ohne Leistungsbuchung" bucht nichts.
     */
    const bucheHandbuch = (serviceId: string | null, hours: number) => {
        const schritt = buchungsAngebot;
        // Bestaetigt — auch „ohne Leistungsbuchung": das Angebot ist erledigt.
        setBuchungsDialog(false);
        setBuchungsAngebot(null);
        if (!serviceId || !schritt || isReadOnly || !onUpdate) return;
        const stand = abschluss.holeBericht();
        if (paketPositionIds(stand?.metadata?.globalData?.services).has(serviceId)) {
            setBuchungsMeldung({ ton: 'fehler', text: PAKET_NICHT_DIREKT });
            return;
        }
        const plan = stand && planeHandbuchBuchung({
            report: stand,
            serviceId,
            hours,
            fassungNr: schritt.fassungNr,
            performerName: autorName || stand.metadata.coordinator || '',
        });
        if (!plan) {
            setBuchungsMeldung({ ton: 'fehler', text: 'Die gewählte Position wurde im Projekt nicht gefunden — es wurde nichts gebucht.' });
            return;
        }
        // Nur in das Projekt der Freigabe — sonst nichts, auch kein Journal.
        const verworfen = abschluss.schreibeFuer(schritt.projektId, plan.report);
        if (verworfen) {
            setBuchungsMeldung({ ton: 'fehler', text: verworfenText(verworfen, 'position') });
            return;
        }
        if (plan.vormerkung) queueJournalMirror(plan.vormerkung);
        else console.warn('[HandbuchModul] Buchung ohne belastbare Projekt-id — nicht im Journal vorgemerkt.');
        const position = (stand.metadata.globalData?.services || []).find(s => s.id === serviceId);
        setBuchungsMeldung({
            ton: 'ok',
            text: `SiGe-Handbuch gebucht: ${plan.logEntry.quantity} ${position?.unit || 'Stück'} auf „${position?.title || 'Position'}“.`,
        });
    };

    const eroeffneNeueFassung = () => {
        if (!handbuch) return;
        if (abgelehntWennNurAnsicht('eine neue Fassung zu eröffnen')) return;
        void mitLauf(async () => {
            const eintrag = {
                nr: naechsteFassungsNummer(handbuch.historie || []),
                datum: heuteIso,
                autor: autorName,
                aenderungen: aenderungsvermerk.trim() || 'Neue Fassung eröffnet.',
            };
            setHandbuch(await neueFassungHandbuch(kontext(), handbuch, eintrag));
            setAenderungsvermerk('');
            melde(`Fassung ${eintrag.nr} ist eröffnet. Die freigegebene Fassung bleibt in der Historie erhalten.`);
            abgleichNeu();
        });
    };

    // ─── Kapitelseiten ───────────────────────────────────────────────────

    /**
     * Kopf und Fuss JEDER Seite.
     *
     * Sie entstehen hier und nicht im Kapitel — das ist die Prop-Konvention
     * dieses Moduls (`kopf`/`fuss` als `ReactNode`, begruendet in
     * `kapitel/A3Seite.tsx`). Der Grund ist die Seitenzahl: „Seite 4 von 11"
     * darf nur EINE Stelle kennen, naemlich `seitenPlan()`. Zaehlte jedes
     * Blatt selbst, kaemen Bildschirm und PDF frueher oder spaeter auf
     * verschiedene Gesamtzahlen — und eine Seitenzahl, die nicht aufgeht,
     * stellt im Streitfall die Vollstaendigkeit des Dokuments in Frage.
     */
    const seitenKopf = (
        <SeitenKopf
            projektName={projektName}
            bauherr={metadata.clientName}
            orgModell={orgModellLabel(handbuch?.org_modell)}
            stand={handbuch?.stand}
            fassung={handbuch?.version_label || undefined}
        />
    );

    const seitenFuss = (eintrag: SeitenEintrag) => (
        <SeitenFuss
            dokument={verbinde(
                ['SiGe-Handbuch', projektName, projektNummer && `Nr. ${projektNummer}`],
                ' | ',
            )}
            ersteller={getRepresentatives(metadata)[0]?.name || metadata.coordinator}
            seite={eintrag.nr}
            gesamtSeiten={seiten.length}
            kapitel={kapitelTitel(eintrag.kapitel)}
            stand={handbuch?.stand}
        />
    );

    /** Eine geaenderte Gewerkeseite schreiben (Anhang II, PSA). */
    const aendereGewerk = (id: string, felder: Partial<HandbuchGewerk>, satz: string) => {
        if (abgelehntWennNurAnsicht(satz)) return;
        void mitLauf(async () => {
            uebernehmeGewerk(await updateHandbuchGewerk(kontext(), id, felder));
            melde('Übernommen.');
        });
    };

    const kapitelSeite = (eintrag: SeitenEintrag): React.ReactNode => {
        const kopf = seitenKopf;
        const fuss = seitenFuss(eintrag);

        switch (eintrag.kapitel) {
            case 'deckblatt':
                return (
                    <Deckblatt
                        /* Ohne `kopf`: das Deckblatt IST der Kopf des Dokuments. */
                        fuss={fuss}
                        metadata={metadata}
                        globalData={globalData}
                        handbuch={handbuchAnsicht}
                        planNr={planNr}
                        planVersion={planVersion}
                        anzahlGewerke={gewerke.length}
                        onSprung={springe}
                    />
                );
            case 'organigramm':
                return (
                    <Organigramm
                        kopf={kopf}
                        fuss={fuss}
                        metadata={metadata}
                        handbuch={handbuchAnsicht}
                        vorankuendigung={vorankuendigung}
                        gewerke={gewerke}
                        planGewerke={planGewerke}
                        nachweise={nachweise}
                        onSprung={springe}
                    />
                );
            case 'baustelleneinrichtung':
                return (
                    <Baustelleneinrichtung
                        kopf={kopf}
                        fuss={fuss}
                        metadata={metadata}
                        handbuch={handbuchAnsicht}
                        schritte={kapitelQuelle.arbeitsschritte}
                        koordinaten={globalData?.locationCoordinates}
                        onSprung={springe}
                    />
                );
            case 'baustellenordnung':
                return (
                    <Baustellenordnung
                        kopf={kopf}
                        fuss={fuss}
                        handbuch={handbuchAnsicht}
                        metadata={metadata}
                        notrufplan={kapitelQuelle.notrufplan ?? undefined}
                        isReadOnly={isReadOnly}
                        /* Die Erfassung schreibt in den ENTWURF, nicht in die
                           Datenbank: `onAendern` feuert je Tastendruck. */
                        onAendern={darfAendern ? setOrdnungEntwurf : undefined}
                        onSprung={springe}
                    />
                );
            case 'gefaehrdungsmatrix':
                return (
                    <Gefaehrdungsmatrix
                        kopf={kopf}
                        fuss={fuss}
                        gewerke={gewerke}
                        paare={kapitelQuelle.paare}
                        /* Die drei plangespeisten Kapitel bekommen den
                           Handbuchsatz, weil der Zeitraum einer FREIGEGEBENEN
                           Fassung nicht mehr aus dem laufenden Plan gerechnet
                           werden darf (`wirksamerZeitraumDerFassung`). Es ist
                           `handbuchAnsicht` und nicht `handbuch`: die Kapitel
                           sehen ueberall denselben Satz, sonst zeigte die
                           Vorschau zwei Staende. */
                        handbuch={handbuchAnsicht}
                        planGewerke={planGewerke}
                        tabelleFehlt={kapitelQuelle.paareTabelleFehlt}
                        onKoordinierungOeffnen={onNavigate ? () => onNavigate('koordinierung') : undefined}
                    />
                );
            case 'gesamtzeitplan':
                return (
                    <Gesamtzeitplan
                        kopf={kopf}
                        fuss={fuss}
                        gewerke={gewerke}
                        handbuch={handbuchAnsicht}
                        planGewerke={planGewerke}
                        paare={kapitelQuelle.paare}
                        projektVon={globalData?.startDate}
                        projektBis={globalData?.endDate}
                    />
                );
            case 'gewerke': {
                const gewerk = gewerke.find(g => g.id === eintrag.gewerkId);
                if (!gewerk) return null;
                return (
                    <GewerkSeite
                        kopf={kopf}
                        fuss={fuss}
                        gewerk={gewerk}
                        handbuch={handbuchAnsicht}
                        alleGewerke={gewerke}
                        nachweise={nachweise.filter(n => n.handbuch_gewerk_id === gewerk.id)}
                        dokumente={kapitelQuelle.dokumente}
                        planGewerke={planGewerke}
                        schritte={kapitelQuelle.arbeitsschritte}
                        gefaehrdungen={kapitelQuelle.gefaehrdungen}
                        massnahmen={kapitelQuelle.massnahmen}
                        notrufplan={kapitelQuelle.notrufplan}
                        paare={kapitelQuelle.paare}
                        anhangIIVorschlag={vorankuendigung?.selectedDangerousWorks}
                        anhangIIPlanung={anhangIIPlanung}
                        heuteIso={heuteIso}
                        projektVon={globalData?.startDate}
                        projektBis={globalData?.endDate}
                        darfAendern={darfAendern}
                        sperrHinweisText={sperrHinweis(handbuch)}
                        onAnhangIIUebernehmen={werte => aendereGewerk(
                            gewerk.id, { anhang_ii: werte }, 'Anhang-II-Arbeiten zu übernehmen',
                        )}
                        onPsaUebernehmen={eintraege => aendereGewerk(
                            gewerk.id, { psa: eintraege }, 'die PSA zu übernehmen',
                        )}
                    />
                );
            }
            case 'dokumentenverzeichnis':
                return (
                    <Dokumentenverzeichnis
                        kopf={kopf}
                        fuss={fuss}
                        dokumente={kapitelQuelle.dokumente}
                        nachweise={nachweise}
                        gewerke={gewerke}
                        heuteIso={heuteIso}
                        onAblageOeffnen={() => setReiter('dokumente')}
                    />
                );
            case 'historie':
                return (
                    <Historie
                        kopf={kopf}
                        fuss={fuss}
                        handbuch={handbuchAnsicht}
                        planFassungen={kapitelQuelle.planFassungen}
                        planUngeladen={!kapitelGeladen}
                    />
                );
            default:
                return null;
        }
    };

    // ─── Ansicht ─────────────────────────────────────────────────────────

    const REITER: { key: Reiter; label: string; icon: React.ReactNode; zahl?: number }[] = [
        { key: 'uebersicht', label: 'Übersicht', icon: <BookOpen className="w-4 h-4" /> },
        { key: 'gewerke', label: 'Gewerke', icon: <HardHat className="w-4 h-4" />, zahl: gewerke.length },
        { key: 'kapitel', label: 'Kapitel', icon: <FileText className="w-4 h-4" />, zahl: seiten.length },
        { key: 'dokumente', label: 'Dokumente', icon: <FolderOpen className="w-4 h-4" /> },
    ];

    return (
        <div data-glass-view="handbuch" className="h-full flex flex-col bg-slate-50 min-h-0">
            {/* Stunden-Pflichtfenster (Stundenkontingent S3) — genau einmal je Modul,
                an der Wurzel (der Hinweis steht nur an der freigegebenen Fassung). */}
            <BestandteilStundenPflicht pflicht={abschluss.stundenPflicht} />
            {/* Abschlussschritt nach der Freigabe (Plan B5, Fall 1): Auswahl aus
                ALLEN Positionen — das Handbuch hat keinen eigenen Zuordnungswert —,
                ohne Vorauswahl, Preise nur mit dem Recht „Preise sehen". */}
            {buchungsAngebot && buchungsDialog && (
                <BillingSelectionModal
                    isOpen
                    onClose={() => setBuchungsDialog(false)}
                    onConfirm={bucheHandbuch}
                    services={allePositionen}
                    title="SiGe-Handbuch abrechnen"
                    context="handbuch"
                    memoryScope={projectId}
                    canSeeFinancials={rechte.hat('preise.sehen')}
                    // Neue Fassung: voll gebuchte Pauschalen sagt der Dialog vorher.
                    vorabHinweis={schonGebuchtHinweis(vollGebuchtePauschalen(allePositionen))}
                    ohneVorauswahl
                />
            )}
            <div className="flex-1 overflow-y-auto min-h-0">
                <div className="max-w-6xl mx-auto px-4 py-6">

                    <div className="flex items-start gap-4 mb-6">
                        <button
                            type="button"
                            onClick={onBack}
                            className="mt-1 p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
                            aria-label="Zurück"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-2xl font-black text-slate-900 leading-tight">SiGe-Handbuch</h1>
                            <p className="text-sm text-slate-500">
                                {projektName || 'Projekt'}
                                {projektNummer && <> · {projektNummer}</>}
                                {' '}· {orgModellLabel(handbuch?.org_modell)}
                            </p>
                        </div>
                        {/* ─── Druck ─────────────────────────────────────
                            Lesend, deshalb auch in Nur-Ansicht und bei
                            freigegebenem Handbuch erlaubt — ein Aushang darf
                            beliebig oft gedruckt werden, er ist kein
                            Einweisungsprotokoll und bucht nichts. */}
                        {handbuch && (
                            <div className="shrink-0 flex items-center gap-2 flex-wrap justify-end">
                                <button
                                    type="button"
                                    onClick={() => drucke('handbuch')}
                                    disabled={!!druckWunsch}
                                    title="Das vollständige SiGe-Handbuch als A3-PDF"
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                                >
                                    <Printer className="w-3.5 h-3.5" /> Handbuch drucken
                                </button>
                                <div className="inline-flex items-stretch rounded-lg border border-slate-300 overflow-hidden bg-white">
                                    <button
                                        type="button"
                                        onClick={() => drucke('aushang')}
                                        disabled={!!druckWunsch}
                                        title="Die Bautafel für den Bauzaun"
                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                                    >
                                        <Printer className="w-3.5 h-3.5" /> Aushang drucken
                                    </button>
                                    <select
                                        value={aushangFormat}
                                        onChange={e => setAushangFormat(e.target.value as AushangFormat)}
                                        aria-label="Format des Aushangs"
                                        title="A3 für die übliche Baustelle, A2 für große Zufahrten. Kein A4 — der Aushang soll aus drei Metern lesbar sein."
                                        className="text-xs font-bold text-slate-600 border-l border-slate-300 px-2 bg-slate-50"
                                    >
                                        <option value="a3">A3</option>
                                        <option value="a2">A2</option>
                                    </select>
                                </div>
                            </div>
                        )}
                        {gesperrt && (
                            <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-bold">
                                <Lock className="w-3.5 h-3.5" /> Freigegeben
                            </span>
                        )}
                    </div>

                    {isReadOnly && (
                        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-800">
                            {readOnlyTitle('Änderungen')}
                        </div>
                    )}
                    {gesperrt && (
                        <div className="mb-4 bg-slate-100 border border-slate-300 rounded-xl px-4 py-2.5 text-sm text-slate-700">
                            {sperrHinweis(handbuch)}
                        </div>
                    )}

                    {/* Aenderungsdienst (Stufe 6.1): weicht die Planungsphase vom
                        Stand der freigegebenen Fassung ab, steht es hier. „Neue
                        Fassung" ist derselbe Weg wie der Knopf in der Freigabe-
                        Karte — nur an einem eingefrorenen Handbuch; ein offener
                        Entwurf braucht keine neue Fassung. */}
                    {handbuch && (
                        <AbweichungsHinweis
                            projectId={projectId}
                            metadata={metadata}
                            unterlage="handbuch"
                            isReadOnly={isReadOnly}
                            onNeueFassung={gesperrt && !isReadOnly ? eroeffneNeueFassung : undefined}
                            neuladen={abgleichLauf}
                            sperrgrund={laeuft || ablageLaeuft
                                ? 'Das Handbuch wird gerade geschrieben bzw. die Fassung abgelegt — ein Entscheid ist erst danach möglich.'
                                : null}
                        />
                    )}

                    {fehler && (
                        <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800">
                            Das Handbuch konnte nicht geladen werden: {fehler}
                        </div>
                    )}
                    {aktionsfehler && (
                        <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span className="flex-1 whitespace-pre-line">{aktionsfehler}</span>
                            <button type="button" onClick={() => setAktionsfehler(null)} className="text-xs font-bold underline shrink-0">Schließen</button>
                        </div>
                    )}
                    {meldung && (
                        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>{meldung}</span>
                        </div>
                    )}

                    {/* ─── Offener Entwurf ───────────────────────────────── */}
                    {offenerEntwurf && (
                        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap sticky top-2 z-10">
                            <span className="text-sm text-blue-900 flex-1 min-w-0">
                                Nicht gespeicherte Angaben
                                {ordnungEntwurf && ' zur Baustellenordnung'}
                                {ordnungEntwurf && einrichtungEntwurf && ' und'}
                                {einrichtungEntwurf && ' zur Baustelleneinrichtung'}
                                . Die Kapitelvorschau zeigt sie bereits.
                                {/* Herkunft, solange es sie noch gibt: nach dem
                                    Speichern ist es der Text des Koordinators. */}
                                {!!kiHerkunft && kiHerkunft.anzahl > 0 && (
                                    <span className="block mt-1 text-[12px] font-bold text-violet-700">
                                        {herkunftSatz(kiHerkunft)}
                                    </span>
                                )}
                                {!!kiEinrichtungHerkunft && kiEinrichtungHerkunft.anzahl > 0 && (
                                    <span className="block mt-1 text-[12px] font-bold text-violet-700">
                                        {einrichtungHerkunftSatz(kiEinrichtungHerkunft)}
                                    </span>
                                )}
                            </span>
                            <button
                                type="button"
                                onClick={verwirfKopfsatz}
                                disabled={laeuft}
                                className="px-3 py-1.5 text-xs font-bold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                            >
                                Verwerfen
                            </button>
                            <button
                                type="button"
                                onClick={speichereKopfsatz}
                                disabled={!darfAendern || laeuft}
                                title={isReadOnly ? readOnlyTitle('Speichern') : undefined}
                                className="px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                            >
                                Speichern
                            </button>
                        </div>
                    )}

                    {/* ─── Noch kein Handbuch ────────────────────────────── */}
                    {!laden && !handbuch && (
                        <div className="bg-white rounded-2xl border border-slate-200 px-6 py-10 flex flex-col items-center text-center">
                            <div className="bg-gradient-to-br from-fuchsia-50 to-fuchsia-100 border border-fuchsia-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                                <BookOpen className="w-6 h-6 text-fuchsia-600" />
                            </div>
                            <p className="text-sm text-slate-500 max-w-lg mb-4">
                                Für dieses Projekt gibt es noch kein SiGe-Handbuch. Es entsteht erst,
                                wenn Sie es hier anlegen — bestehende Projekte werden nicht nachträglich
                                verändert.
                            </p>
                            <button
                                type="button"
                                onClick={legeAn}
                                disabled={isReadOnly || laeuft}
                                title={isReadOnly ? readOnlyTitle('Anlegen') : undefined}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                            >
                                <Plus className="w-4 h-4" /> Handbuch anlegen
                            </button>
                        </div>
                    )}

                    {laden && <p className="text-sm text-slate-400 py-10 text-center">Wird geladen …</p>}

                    {!laden && handbuch && (
                        <>
                            {/* ─── Kennzahlen ────────────────────────────── */}
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                                {[
                                    { wert: `${quote} %`, label: 'Kapitel vollständig', farbe: 'text-blue-600' },
                                    { wert: gewerke.length, label: 'Gewerkeseiten', farbe: 'text-slate-800' },
                                    { wert: seiten.length, label: 'Seiten im Druck', farbe: 'text-slate-800' },
                                    { wert: `${nachweisLage.quote} %`, label: 'Nachweise vorhanden', farbe: 'text-emerald-600' },
                                    { wert: nachweisLage.ueberfaellig, label: 'überfällig', farbe: nachweisLage.ueberfaellig ? 'text-rose-600' : 'text-slate-400' },
                                ].map(k => (
                                    <div key={k.label} className="bg-white rounded-xl border border-slate-200 px-4 py-3">
                                        <div className={`text-2xl font-black ${k.farbe}`}>{k.wert}</div>
                                        <div className="text-[11px] text-slate-400 font-bold uppercase tracking-wide">{k.label}</div>
                                    </div>
                                ))}
                            </div>

                            {/* ─── Reiter ────────────────────────────────── */}
                            <div className="flex items-center gap-1 border-b border-slate-200 mb-6 flex-wrap">
                                {REITER.map(r => (
                                    <button
                                        key={r.key}
                                        type="button"
                                        onClick={() => setReiter(r.key)}
                                        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px whitespace-nowrap transition-colors ${
                                            reiter === r.key
                                                ? 'border-blue-600 text-blue-600'
                                                : 'border-transparent text-slate-500 hover:text-slate-700'
                                        }`}
                                    >
                                        {r.icon} {r.label}
                                        {typeof r.zahl === 'number' && r.zahl > 0 && (
                                            <span className="text-[10px] font-black bg-slate-100 text-slate-500 rounded-full px-1.5 py-0.5">{r.zahl}</span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* ─── Übersicht ─────────────────────────────── */}
                            {reiter === 'uebersicht' && (
                                <div className="space-y-4">
                                    {/* Das Vergabemodell steht ganz oben, weil ohne es weder
                                        Deckblatt noch Organigramm entstehen koennen. */}
                                    <div className="bg-white rounded-2xl border border-slate-200 p-5">
                                        <h3 className="font-bold text-slate-800 text-sm mb-1">Vergabemodell</h3>
                                        <p className="text-xs text-slate-500 mb-3">
                                            Es entscheidet, wer im Organigramm weisungsbefugt ist. Ohne Angabe
                                            bleibt das Organigramm leer — geraten wird hier nichts.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {ORG_MODELL_REIHENFOLGE.map(m => (
                                                <button
                                                    key={m}
                                                    type="button"
                                                    onClick={() => setzeOrgModell(m)}
                                                    disabled={isReadOnly || gesperrt || laeuft}
                                                    title={ORG_MODELLE[m].beschreibung}
                                                    className={`px-3 py-1.5 text-xs font-bold rounded-lg border disabled:opacity-40 ${
                                                        handbuch.org_modell === m
                                                            ? 'bg-blue-600 text-white border-blue-600'
                                                            : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                                                    }`}
                                                >
                                                    {ORG_MODELLE[m].label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* ─── Baustelleneinrichtung ─────────────
                                        Die Felder gehoeren zum Kopfsatz
                                        (`handbuecher.einrichtung`) — genau wie
                                        das Vergabemodell darueber, und deshalb
                                        stehen sie hier und nicht im Kapitel:
                                        das Kapitelblatt ist die DRUCKANSICHT
                                        und liest sie nur. Der Lageplan selbst
                                        bleibt, wo er ist (`metadata.plans`);
                                        hier steht nur, was ihn erklaert.

                                        ═══ DREI HILFEN STATT FUENF LEERER FELDER ═══
                                        Zuvor standen hier fuenf Textfelder mit
                                        einem Beispiel im Platzhalter. Wer sie
                                        zum ersten Mal sah, wusste weder was
                                        hineingehoert noch wieviel: die Felder
                                        landen in Kapitel 3 in zwei schmalen
                                        Spalten mit `overflow: hidden`, und was
                                        dort nicht hinpasst, faellt beim Druck
                                        ab, ohne dass es jemand meldet. Deshalb
                                        jetzt: eine Frage je Feld, anklickbare
                                        Bausteine, und ein Laengenmass gegen das
                                        Abschneiden. Die Regeln dazu stehen in
                                        `einrichtungBausteine.ts` — dort sind
                                        sie geprueft. */}
                                    <div className="bg-white rounded-2xl border border-slate-200 p-5">
                                        <div className="flex items-start justify-between gap-3 mb-1">
                                            <h3 className="font-bold text-slate-800 text-sm">Baustelleneinrichtung</h3>
                                            <button
                                                type="button"
                                                onClick={erzeugeVorschlag}
                                                disabled={!darfAendern || laeuft || vorschlagLaeuft}
                                                title={darfAendern
                                                    ? 'Leitet aus den Gewerken und gemeinsam genutzten Einrichtungen '
                                                        + 'des SiGe-Plans Sätze ab. Übernommen wird nichts von allein.'
                                                    : undefined}
                                                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-40"
                                            >
                                                <Wand2 className="w-3.5 h-3.5" />
                                                {vorschlagLaeuft ? 'Liest den Plan …' : 'Vorschlag aus Projektdaten'}
                                            </button>
                                        </div>
                                        <p className="text-xs text-slate-500 mb-3">
                                            Vier Festlegungen zum Baustelleneinrichtungsplan — sie stehen später in
                                            Kapitel 3 neben dem Lageplan. Gefragt ist jeweils, WER etwas stellt und
                                            WO es steht, nicht eine Aufzählung von Stichworten.
                                        </p>

                                        {/* Die Abgrenzung zum SiGe-Plan stand bisher im Einleitungssatz
                                            und erklaerte als Erstes, was hier NICHT passiert. Sie ist
                                            richtig und bleibt — aber eine Ebene tiefer, fuer den, der
                                            sie sucht. */}
                                        <details className="mb-3 text-xs">
                                            <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                                                Warum stehen Gerüst, Kran und Verkehrswege nicht hier?
                                            </summary>
                                            <p className="mt-1.5 pl-3 border-l-2 border-slate-200 text-slate-500 leading-relaxed">
                                                Die gemeinsam genutzten Einrichtungen leitet das Kapitel aus den
                                                Arbeitsschritten des SiGe-Plans ab — aus denen mit einem
                                                Hauptabschnitt „gemeinsam genutzte Einrichtung: …". Sie werden hier
                                                nicht erfasst, sonst liefen Plan und Handbuch auseinander und zwei
                                                Papiere stritten darüber, wer das Gerüst mitbenutzt.
                                            </p>
                                        </details>

                                        {/* ─── Der offene Vorschlag ───────────
                                            Er wird ANGEZEIGT und nicht eingeschrieben: jede
                                            Zeile mit ihrer Begruendung, darunter das, was das
                                            Regelwerk nicht wissen kann. Ohne diese zweite
                                            Liste erzeugte das Werkzeug einen vollstaendig
                                            aussehenden Text ueber eine Baustelle, die niemand
                                            gesehen hat. */}
                                        {vorschlag && (
                                            <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/60 p-4">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <h4 className="text-xs font-black uppercase tracking-wider text-blue-800">
                                                            Vorschlag aus den Projektdaten
                                                        </h4>
                                                        <p className="text-[11px] text-blue-900/70 mt-0.5">
                                                            {vorschlag.zeilen.length} Satz/Sätze. Abwählen, was nicht passt —
                                                            übernommen wird in den Entwurf, gespeichert erst über den
                                                            blauen Balken.
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setVorschlag(null)}
                                                        className="shrink-0 text-[11px] font-bold text-slate-500 hover:text-slate-700"
                                                    >
                                                        Schließen
                                                    </button>
                                                </div>

                                                {EINRICHTUNGSFELDER.map(f => {
                                                    const zeilen = vorschlag.zeilen.filter(z => z.feld === f.feld);
                                                    if (!zeilen.length) return null;
                                                    return (
                                                        <div key={f.feld} className="mt-3">
                                                            <div className="text-[10px] font-black uppercase tracking-wider text-blue-900/60">
                                                                {f.label}
                                                            </div>
                                                            {zeilen.map(z => (
                                                                <label key={z.text} className="mt-1 flex items-start gap-2 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={vorschlagAuswahl.includes(z.text)}
                                                                        onChange={e => setVorschlagAuswahl(alt => (e.target.checked
                                                                            ? [...alt, z.text]
                                                                            : alt.filter(t => t !== z.text)))}
                                                                        className="mt-0.5 shrink-0"
                                                                    />
                                                                    <span className="min-w-0">
                                                                        <span className="block text-xs text-slate-700 leading-snug">{z.text}</span>
                                                                        <span className="block text-[10px] text-slate-500 mt-0.5">{z.grund}</span>
                                                                    </span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    );
                                                })}

                                                <div className="mt-3 pt-3 border-t border-blue-200">
                                                    <div className="text-[10px] font-black uppercase tracking-wider text-amber-800">
                                                        Was der Vorschlag nicht weiß
                                                    </div>
                                                    <ul className="mt-1 text-[11px] text-amber-900/80 list-disc list-inside space-y-0.5 leading-snug">
                                                        {vorschlag.offen.map((o, i) => <li key={i}>{o}</li>)}
                                                    </ul>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={uebernimmVorschlag}
                                                    disabled={!darfAendern || vorschlagAuswahl.length === 0}
                                                    className="mt-3 px-3 py-2 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                                                >
                                                    {vorschlagAuswahl.length} Satz/Sätze in den Entwurf übernehmen
                                                </button>
                                            </div>
                                        )}

                                        <div className="grid sm:grid-cols-2 gap-4">
                                            {EINRICHTUNGSFELDER.map(f => {
                                                // Das Kapitel setzt das Label FETT vor den Text in
                                                // dieselbe Zeile; ohne diesen Abzug meldete die
                                                // Anzeige Platz, den es auf dem Blatt nicht gibt.
                                                const belegt = druckZeilen(
                                                    einrichtung[f.feld],
                                                    f.feld === 'bemerkung' ? 0 : f.label.length + 2,
                                                );
                                                const zuLang = belegt > f.druckZeilen;
                                                return (
                                                    <div key={f.feld} className={f.breit ? 'sm:col-span-2' : ''}>
                                                        <div className="flex items-baseline justify-between gap-2">
                                                            <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                                                {f.label}
                                                            </span>
                                                            <span
                                                                title={zuLang
                                                                    ? 'Das Kapitel schneidet ab, was nicht auf die Seite passt.'
                                                                    : 'Platz in der Kapitelspalte'}
                                                                className={`text-[10px] font-bold ${zuLang ? 'text-amber-600' : 'text-slate-400'}`}
                                                            >
                                                                {belegt} / {f.druckZeilen} Druckzeilen
                                                            </span>
                                                        </div>
                                                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{f.merksatz}</p>
                                                        <textarea
                                                            value={einrichtung[f.feld]}
                                                            onChange={e => setzeEinrichtung(f.feld, e.target.value)}
                                                            rows={f.zeilen}
                                                            disabled={!darfAendern || laeuft}
                                                            className={`mt-1 w-full text-sm rounded-lg border px-3 py-2 disabled:bg-slate-100 ${
                                                                zuLang ? 'border-amber-300' : 'border-slate-300'
                                                            }`}
                                                        />
                                                        {zuLang && (
                                                            <p className="text-[10px] text-amber-700 mt-0.5 leading-snug">
                                                                Länger als die Spalte in Kapitel 3 — was nicht passt, wird
                                                                beim Druck abgeschnitten.
                                                            </p>
                                                        )}
                                                        {darfAendern && (
                                                            <div className="mt-1.5 flex flex-wrap gap-1">
                                                                {bausteineZuFeld(f.feld).map(b => (
                                                                    <button
                                                                        key={b.id}
                                                                        type="button"
                                                                        onClick={() => klickeBaustein(b)}
                                                                        title={b.text}
                                                                        className="px-2 py-0.5 text-[10px] font-bold text-slate-600 bg-slate-100 border border-slate-200 rounded-full hover:bg-slate-200"
                                                                    >
                                                                        + {b.kurz}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <p className="text-[10px] text-slate-400 mt-1">↳ {f.ziel}</p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {!darfAendern && (
                                            <p className="mt-2 text-xs text-slate-400">
                                                {gesperrt
                                                    ? sperrHinweis(handbuch)
                                                    : readOnlyTitle('Änderungen')}
                                            </p>
                                        )}
                                    </div>

                                    <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
                                        {befunde.map(b => (
                                            <div key={b.kapitel} className="px-5 py-3 flex items-start gap-3">
                                                <span className="mt-0.5 shrink-0">
                                                    <KapitelZeichen vollstaendig={b.vollstaendig} pflicht={b.pflicht} />
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-bold text-slate-800">
                                                        {b.titel}
                                                        {b.pflicht && (
                                                            <span className="ml-2 text-[10px] font-black uppercase tracking-wider text-slate-400">Pflicht</span>
                                                        )}
                                                    </div>
                                                    {b.fehlend.length > 0 && (
                                                        <ul className="mt-1 text-xs text-slate-500 list-disc list-inside space-y-0.5">
                                                            {b.fehlend.slice(0, 5).map((f, i) => <li key={i}>{f}</li>)}
                                                            {b.fehlend.length > 5 && (
                                                                <li className="list-none text-slate-400">… und {b.fehlend.length - 5} weitere</li>
                                                            )}
                                                        </ul>
                                                    )}
                                                    {b.hinweis && (
                                                        <p className="mt-1 text-xs text-slate-400">{b.hinweis}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* ─── Freigabe ──────────────────────── */}
                                    <div className={`rounded-2xl border p-5 ${
                                        gesperrt ? 'bg-slate-50 border-slate-300'
                                            : freigabe.freigabeMoeglich ? 'bg-emerald-50 border-emerald-200'
                                                : 'bg-amber-50 border-amber-200'
                                    }`}>
                                        <div className="flex items-start gap-3">
                                            {freigabe.freigabeMoeglich
                                                ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                                                : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
                                            <div className="min-w-0 flex-1">
                                                <h3 className="font-bold text-slate-800 text-sm mb-1">
                                                    {gesperrt ? 'Freigegebene Fassung' : 'Freigabe des Handbuchs'}
                                                </h3>
                                                <p className="text-xs text-slate-600 leading-snug">{freigabe.hinweis}</p>
                                                {freigabe.offenePunkte.length > 0 && (
                                                    <ul className="mt-2 text-[11px] text-amber-900 list-disc list-inside space-y-0.5">
                                                        {freigabe.offenePunkte.slice(0, 6).map((p, i) => <li key={i}>{p}</li>)}
                                                    </ul>
                                                )}
                                                <label className="block mt-3">
                                                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                                        Änderungsvermerk für die Historie
                                                    </span>
                                                    <textarea
                                                        value={aenderungsvermerk}
                                                        onChange={e => setAenderungsvermerk(e.target.value)}
                                                        rows={2}
                                                        disabled={isReadOnly || laeuft}
                                                        placeholder="Was hat sich gegenüber der letzten Fassung geändert?"
                                                        className="mt-1 w-full text-sm rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
                                                    />
                                                </label>
                                                {/* Zentrale Ablage (Stufe 1A): die Freigabe legt automatisch
                                                    ab; der Knopf holt es nach bzw. legt einen Entwurf ab. */}
                                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                                    <button
                                                        type="button"
                                                        onClick={legeAktuellenStandAb}
                                                        disabled={isReadOnly || laeuft || ablageLaeuft}
                                                        title={isReadOnly
                                                            ? readOnlyTitle('In Dokumente ablegen')
                                                            : 'Das Handbuch-PDF als eigene Fassung in der Projektablage (Dokumente) ablegen'}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                                                    >
                                                        <FolderUp className="w-3.5 h-3.5" />
                                                        {gesperrt ? 'Fassung in Dokumente ablegen' : 'Entwurf in Dokumente ablegen'}
                                                    </button>
                                                    {(ablageLaeuft || ablage) && (
                                                        <span className={`text-[11px] inline-flex items-center gap-1 ${
                                                            ablageLaeuft ? ABLAGE_TON_KLASSE.laeuft : ABLAGE_TON_KLASSE[ablageStandTon(ablage?.stand)]
                                                        }`}>
                                                            {ablageLaeuft
                                                                ? ablageStandText({ art: 'laeuft' })
                                                                : ablageStandText(ablage?.stand)}
                                                            {!ablageLaeuft && ablage?.stand.art === 'fehler' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={erneutAblegen}
                                                                    className="underline font-bold"
                                                                >
                                                                    Erneut ablegen
                                                                </button>
                                                            )}
                                                        </span>
                                                    )}
                                                    {/* Anmerkungen des Kerns — dezent, nie im Fehlerton: die Fassung liegt. */}
                                                    {!ablageLaeuft && !!ablage?.hinweise?.length && (
                                                        <p className="basis-full text-[11px] text-slate-500 leading-snug">
                                                            {ablage.hinweise.join(' · ')}
                                                        </p>
                                                    )}
                                                    {/* Abschlussschritt (Plan B5): Ergebnis der Buchung und was die
                                                        Freigabe an Bestandteilen einer Paketposition erledigt hat. */}
                                                    {buchungsMeldung && (
                                                        <p className={`basis-full text-[11px] leading-snug ${buchungsMeldung.ton === 'ok' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                            {buchungsMeldung.text}
                                                        </p>
                                                    )}
                                                    {/* Fall 1 (Plan B5): das Angebot statt eines automatischen Dialogs. */}
                                                    {buchungsAngebot && !isReadOnly && (
                                                        <p className="basis-full text-[11px] text-slate-600 leading-snug flex flex-wrap items-center gap-x-2 gap-y-1">
                                                            <span>Fassung {buchungsAngebot.fassungNr} freigegeben. Steht das Handbuch als eigene Leistung im LV, hier buchen:</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setBuchungsDialog(true)}
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 font-bold text-emerald-800 bg-white border border-emerald-300 rounded hover:bg-emerald-50"
                                                            >
                                                                Leistung buchen
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setBuchungsAngebot(null)}
                                                                className="underline text-slate-500"
                                                            >
                                                                Ausblenden
                                                            </button>
                                                        </p>
                                                    )}
                                                    {abschluss.stand && (
                                                        <div className="basis-full">
                                                            <BestandteilAbschlussHinweis stand={abschluss.stand} onSchliessen={abschluss.zuruecksetzen} />
                                                        </div>
                                                    )}
                                                    {/* Das Freigabe-Journal (Stufe 6.1) konnte nicht geschrieben werden —
                                                        die Freigabe gilt, die Fassung wird aber nicht ueberwacht. */}
                                                    {freigabeJournal && (
                                                        <p className="basis-full text-[11px] text-amber-700 leading-snug">
                                                            {freigabeJournal.hinweis}
                                                            {freigabeJournal.wiederholung && !isReadOnly && (
                                                                <>
                                                                    {' '}
                                                                    <button
                                                                        type="button"
                                                                        onClick={halteFreigabeErneutFest}
                                                                        disabled={laeuft}
                                                                        className="underline font-bold disabled:opacity-50"
                                                                    >
                                                                        Erneut festhalten
                                                                    </button>
                                                                </>
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="shrink-0">
                                                {gesperrt ? (
                                                    <button
                                                        type="button"
                                                        onClick={eroeffneNeueFassung}
                                                        disabled={isReadOnly || laeuft}
                                                        title={isReadOnly ? readOnlyTitle('Neue Fassung') : undefined}
                                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                                                    >
                                                        <Unlock className="w-3.5 h-3.5" /> Neue Fassung
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={gibFrei}
                                                        disabled={isReadOnly || laeuft || !freigabe.freigabeMoeglich}
                                                        title={isReadOnly ? readOnlyTitle('Freigeben') : undefined}
                                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-40"
                                                    >
                                                        <Lock className="w-3.5 h-3.5" /> Freigeben
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ─── Gewerke ───────────────────────────────── */}
                            {reiter === 'gewerke' && (
                                <div className="space-y-4">
                                    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
                                        <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Aus dem SiGe-Plan</span>
                                        <button
                                            type="button"
                                            onClick={holePlan}
                                            disabled={laeuft || planLaeuft}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                                        >
                                            <RefreshCw className="w-3.5 h-3.5" /> Plan einlesen
                                        </button>
                                        {planGeladen && abgleich.neu.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={zieheNach}
                                                disabled={isReadOnly || gesperrt || laeuft}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                                            >
                                                <Plus className="w-3.5 h-3.5" /> {abgleich.neu.length} Gewerkeseite(n) anlegen
                                            </button>
                                        )}
                                        {planGeladen && abgleich.neu.length === 0 && (
                                            <span className="text-xs text-slate-400">Alle Gewerke des Plans haben eine Seite.</span>
                                        )}
                                    </div>

                                    {abgleich.verwaist.length > 0 && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-sm text-amber-900">
                                            {abgleich.verwaist.length} Gewerkeseite(n) haben im aktuellen Plan keine
                                            Entsprechung mehr. Sie bleiben stehen — die dort gesammelten Nachweise
                                            gehen nicht verloren.
                                        </div>
                                    )}

                                    {gewerke.length === 0 ? (
                                        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                                            <HardHat className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                            <p className="text-sm text-slate-500">
                                                Noch keine Gewerkeseite. Lesen Sie den SiGe-Plan ein — daraus
                                                entstehen die Seiten mit ihren Vorlagen.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
                                            {gewerke.map(g => {
                                                const eigene = nachweise.filter(n => n.handbuch_gewerk_id === g.id);
                                                const offen = eigene.filter(n => n.status !== 'vorhanden').length;
                                                return (
                                                    <div key={g.id} className="px-5 py-3 flex items-center gap-3 flex-wrap">
                                                        <div className="min-w-0 flex-1">
                                                            <div className="text-sm font-bold text-slate-800">{g.gewerk_label}</div>
                                                            <div className="text-xs text-slate-400">
                                                                {g.firma || 'Firma noch offen'}
                                                                {g.din_nummer ? ` · ${g.din_nummer}` : ''}
                                                            </div>
                                                        </div>
                                                        <span className="text-xs text-slate-500 shrink-0">
                                                            {eigene.length} Nachweis(e)
                                                            {offen > 0 && <span className="text-amber-700 font-bold"> · {offen} offen</span>}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {planGeladen && planGewerke.some(g => !g.katalogId) && (
                                        <div className="bg-white rounded-2xl border border-slate-200 p-4">
                                            <h3 className="text-sm font-bold text-slate-800 mb-2">Nicht zugeordnete Gewerke</h3>
                                            <p className="text-xs text-slate-500 mb-2">
                                                Für diese Schreibweisen gibt es keine eindeutige Vorlage. Die Seiten
                                                entstehen trotzdem — nur ohne Vorbelegung.
                                            </p>
                                            <ul className="text-xs text-slate-600 space-y-1">
                                                {planGewerke.filter(g => !g.katalogId).map(g => (
                                                    <li key={g.gewerk_norm}>
                                                        <span className="font-bold">{g.gewerk_label}</span>
                                                        {' — '}{katalogHinweis(g.katalogId)}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ─── Kapitel ───────────────────────────────── */}
                            {reiter === 'kapitel' && (
                                <div className="space-y-6">
                                    {/* Seitenspiegel. Er steht ueber den Blaettern und nicht
                                        daneben: die A3-Seiten nehmen die volle Breite, und eine
                                        Spalte daneben draengte sie auf Briefformat. */}
                                    <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 print:hidden">
                                        <div className="px-5 py-3 text-xs text-slate-500">
                                            Seitenspiegel des gedruckten Handbuchs (A3 quer). Die Zählung stammt aus
                                            <span className="font-mono"> seitenPlan()</span> und ist dieselbe, die Kopf
                                            und Fuß jeder Seite tragen — Vorschau und PDF zählen nie getrennt.
                                        </div>
                                        {seiten.map(s => (
                                            <button
                                                key={`spiegel-${s.kapitel}-${s.nr}`}
                                                type="button"
                                                onClick={() => document
                                                    .getElementById(`handbuch-seite-${s.nr}`)
                                                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                                className="w-full px-5 py-2.5 flex items-center gap-3 text-left hover:bg-slate-50"
                                            >
                                                <span className="w-8 text-right text-xs font-black text-slate-300 shrink-0">{s.nr}</span>
                                                <span className="text-sm text-slate-700 flex-1 min-w-0 truncate">{s.titel}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {kapitelLaeuft && !kapitelGeladen && (
                                        <p className="text-sm text-slate-400 py-6 text-center print:hidden">
                                            Plan, Bewertungen und Ablage werden geladen …
                                        </p>
                                    )}
                                    {kapitelGeladen && !kapitelQuelle.sigeProject && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-900 print:hidden">
                                            Für dieses Projekt gibt es keine SiGe-Planfassung. Gefährdungsmatrix,
                                            Gesamtzeitplan und die Tätigkeiten der Gewerkeseiten bleiben deshalb
                                            leer — das ist keine Entwarnung, sondern eine fehlende Quelle.
                                        </div>
                                    )}

                                    {seiten.map(s => (
                                        <div key={`seite-${s.kapitel}-${s.nr}`} id={`handbuch-seite-${s.nr}`}>
                                            {kapitelSeite(s)}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* ─── Dokumente ─────────────────────────────── */}
                            {reiter === 'dokumente' && (
                                <DokumentePanel
                                    projectId={projectId}
                                    modul="handbuch"
                                    userProfile={userProfile}
                                    isReadOnly={isReadOnly}
                                    onSchliessen={() => setReiter('uebersicht')}
                                />
                            )}
                        </>
                    )}

                </div>
            </div>
        </div>
    );
};
